import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Plus, LogOut, ChevronRight, Car, Search, Library, Sparkles, Presentation, Target, LifeBuoy,
  Settings as SettingsIcon, Inbox, TrendingUp, Calendar, MessageSquare,
  Wrench, Users2, Gauge, AlertTriangle, ClipboardCheck, Video, Send, Tag,
  CheckCircle2, Check, Clock, ArrowRight, BarChart3, PoundSterling, Eye, RefreshCcw,
  BellPlus, CalendarClock, Trophy, Flame, Zap as ZapIcon, Loader2, Camera, Upload,
  ShieldCheck, Users, Building2, Trash2, Cloud, Coffee, Sparkles as SparklesIcon,
  Cake, ChevronDown, ChevronUp, UserPlus, Archive as ArchiveIcon,
} from "lucide-react";
import { toast } from "sonner";
import { firstName } from "@/lib/utils";
import { BrandLink } from "@/components/Brand";
import SettingsModal from "@/components/SettingsModal";
import FollowUpsModal from "@/components/FollowUpsModal";
import DiaryImportWizard from "@/components/DiaryImportWizard";
import CallbackQueuePanel from "@/components/CallbackQueuePanel";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import OnboardingBillingBanner from "@/components/OnboardingBillingBanner";
import ThemeToggle from "@/components/ThemeToggle";
import PushOptInPrompt from "@/components/PushOptInPrompt";
import SmsCostTile from "@/components/SmsCostTile";
import PlanConversionTile from "@/components/PlanConversionTile";
import WorkshopSchedulePanel from "@/components/WorkshopSchedulePanel";
import SlaCountdown from "@/components/SlaCountdown";
import PitchDeckSender from "@/components/PitchDeckSender";
import CheckInPhotosModal from "@/components/CheckInPhotosModal";
import CustomerMessagesInbox from "@/components/CustomerMessagesInbox";
import PreArrivalRepliesInbox from "@/components/PreArrivalRepliesInbox";
import NoShowAlertsPanel from "@/components/NoShowAlertsPanel";
import DemoResetButton from "@/components/DemoResetButton";
import PitchDeckFab from "@/components/PitchDeckFab";
import WipeAllJobsButton from "@/components/WipeAllJobsButton";
import UsedCarManagerTab from "@/components/UsedCarManagerTab";
import HeaderMoreMenu from "@/components/HeaderMoreMenu";
import LeaderboardWidget from "@/components/LeaderboardWidget";
import { useSearchParams } from "react-router-dom";

const STATUS_STYLES = {
  draft:    "bg-secondary text-secondary-foreground",
  sent:     "bg-primary/10 text-primary",
  viewed:   "bg-warning/10 text-warning",
  approved: "bg-success/10 text-success",
  partial:  "bg-warning/10 text-warning",
  rejected: "bg-destructive/10 text-destructive",
};

const TABS = [
  { id: "advisor",    label: "Service Advisor", icon: Users2,    blurb: "Front-of-house — pre-calls, messages, quotes" },
  { id: "control",    label: "Workshop Control", icon: Gauge,    blurb: "Today's diary, alerts and live conversion" },
  { id: "technician", label: "Technician",       icon: Wrench,   blurb: "Just your jobs, ready to record" },
  { id: "manager",    label: "Manager",          icon: BarChart3, blurb: "Conversion analytics, revenue and reconsider rate" },
  // UCM (Used Car Manager) tab is intentionally HIDDEN from the nav.
  // Route still works for whoever knows the URL — open with ?tab=ucm.
  // Use it as a "wait, there's more" reveal in pitch conversations.
];

/**
 * A small warm hello at the top of the dashboard.
 *
 * Picks a greeting based on the hour in the UK (Europe/London — handles
 * GMT/BST automatically) and uses just the user's first name. Falls back
 * to a generic "Welcome back" until the user object hydrates, so the
 * layout never wobbles.
 */
function greetingFor(fullName) {
  const first = (fullName || "").trim().split(/\s+/)[0] || "";
  // Always read the hour in London time — workshop is UK-based, and we want
  // a technician on holiday abroad to still see the right greeting for the
  // shift back home.
  const hour = parseInt(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      hour12: false,
    }).format(new Date()),
    10,
  );
  let prefix;
  if (hour >= 5 && hour < 12) prefix = "Good morning";
  else if (hour < 17) prefix = "Good afternoon";
  else if (hour < 22) prefix = "Good evening";
  else prefix = "Working late";  // 22:00–04:59 — gentle nod for the night owls
  return first ? `${prefix}, ${first}` : "Welcome back";
}

function ImpersonationBanner() {
  const banner = (() => {
    try { return JSON.parse(sessionStorage.getItem("impersonate_banner") || "null"); } catch { return null; }
  })();
  if (!banner?.as_email) return null;
  const exit = () => {
    sessionStorage.removeItem("impersonate_banner");
    localStorage.removeItem("token");
    window.location.href = "/admin";
  };
  return (
    <div className="bg-warning/15 border-b-2 border-warning/40 text-warning-foreground" data-testid="impersonation-banner">
      <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-2 flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="inline-flex items-center gap-2 text-warning">
          <ShieldCheck className="w-4 h-4" />
          <span className="font-bold">Viewing as {banner.as_name || banner.as_email}</span>
          <span className="text-xs opacity-80 hidden sm:inline">({banner.as_email}) · all actions are recorded in the audit log</span>
        </div>
        <button onClick={exit} className="px-3 py-1 rounded-md bg-warning text-warning-foreground text-xs font-bold hover:bg-warning/90 transition-colors" data-testid="impersonation-exit-btn">
          Exit impersonation
        </button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [showDiary, setShowDiary] = useState(false);
  const [followUpDueCount, setFollowUpDueCount] = useState(0);
  const [preCallPending, setPreCallPending] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [precallRepliesToday, setPrecallRepliesToday] = useState(0);
  // Tab state — read from URL query first, fallback to localStorage, then "advisor".
  // This lets a bookmarked URL like /dashboard?tab=manager land directly on the
  // manager view without an extra click. Updates to URL stay in sync so links
  // are sharable.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["advisor", "control", "technician", "manager"];
  const initialTab = (() => {
    const fromUrl = searchParams.get("tab");
    if (fromUrl && VALID_TABS.includes(fromUrl)) return fromUrl;
    const fromStorage = localStorage.getItem("dash_tab");
    if (fromStorage && VALID_TABS.includes(fromStorage)) return fromStorage;
    return "advisor";
  })();
  const [tab, setTab] = useState(initialTab);
  const [newJob, setNewJob] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_reg: "",
    job_type: "dropoff",
    service_category: "service",
    service_categories: ["service"],
    loan_car_provided: false,
    customer_waiting: false,
    collection_delivery: false,
    express_service: false,
    appointment_time: "", priority: "normal",
  });

  useEffect(() => {
    localStorage.setItem("dash_tab", tab);
    // Sync the URL ?tab= param so a hard refresh or bookmark keeps the tab.
    // Replace (not push) so the back button still escapes the dashboard rather
    // than walking back through every tab the user clicked.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "advisor") next.delete("tab");  // keep default URL clean
        else next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }, [tab, setSearchParams]);

  // If the URL is changed externally (browser back, deep-link click), follow it.
  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (fromUrl && VALID_TABS.includes(fromUrl) && fromUrl !== tab) {
      setTab(fromUrl);
    }
    // VALID_TABS is a stable constant — including in deps would just churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jobs");
      setJobs(r.data);
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Auto-refresh jobs every 30s while the Workshop Control tab is open so
  // "Workshop complete" alerts from the tech's iPad reach the advisor in
  // near-real-time. Cheap poll — one endpoint, keeps the UI honest.
  useEffect(() => {
    if (tab !== "control") return;
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/follow-ups").then((r) => { if (!cancelled) setFollowUpDueCount(r.data.count || 0); }).catch(() => {});
      api.get("/pre-calls").then((r) => { if (!cancelled) setPreCallPending(r.data.pending || 0); }).catch(() => {});
      api.get("/messages/unread-count").then((r) => { if (!cancelled) setUnreadMessages(r.data.unread_count || 0); }).catch(() => {});
      api.get("/precall/submissions-today").then((r) => { if (!cancelled) setPrecallRepliesToday(r.data.count || 0); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const createJob = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...newJob, vehicle_year: newJob.vehicle_year ? Number(newJob.vehicle_year) : null };
      const r = await api.post("/jobs", payload);
      toast.success("Job created");
      nav(`/jobs/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const createDemo = async () => {
    setDemoBusy(true);
    try {
      const r = await api.post("/jobs/demo");
      toast.success("Demo job created");
      nav(`/jobs/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setDemoBusy(false); }
  };

  const matches = useCallback((j) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (j.customer_name || "").toLowerCase().includes(s) ||
           (j.vehicle_reg || "").toLowerCase().includes(s) ||
           (j.vehicle_make || "").toLowerCase().includes(s) ||
           (j.vehicle_model || "").toLowerCase().includes(s);
  }, [q]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <BrandLink />
          <div className="flex items-center gap-2 sm:gap-3">
            {user?.platform_owner && (
              <Link
                to="/accounts"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-warning/40 bg-warning/[0.06] hover:bg-warning/[0.12] text-warning text-sm font-bold transition-colors"
                data-testid="nav-admin-btn"
                title="Internal accounts — platform owner only"
              >
                <ShieldCheck className="w-4 h-4" /> <span className="hidden lg:inline">Accounts</span>
              </Link>
            )}
            {(user?.is_group_manager || user?.platform_owner) && (
              <Link
                to="/group"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/40 bg-primary/[0.06] hover:bg-primary/[0.12] text-primary text-sm font-bold transition-colors"
                data-testid="nav-group-btn"
                title="Group dashboard — read-only across all dealerships"
              >
                <Building2 className="w-4 h-4" /> <span className="hidden lg:inline">Group</span>
              </Link>
            )}
            <ThemeToggle />
            <Link to="/team" className="btn-outline" data-testid="nav-team-btn" title="My team">
              <Users className="w-4 h-4" /> <span className="hidden lg:inline">Team</span>
            </Link>
            <button onClick={() => setShowSettings(true)} className="btn-outline" data-testid="nav-settings-btn" title="Workshop settings">
              <SettingsIcon className="w-4 h-4" /> <span className="hidden lg:inline">Settings</span>
            </button>
            {/* Secondary nav — low-frequency actions tucked in a "More ▾"
                dropdown so the header row never wraps on 1366px monitors. */}
            <HeaderMoreMenu
              items={[
                {
                  id: "how-it-works",
                  label: "How it works",
                  icon: Presentation,
                  to: "/how-it-works",
                },
                {
                  id: "help",
                  label: "Help & training",
                  icon: LifeBuoy,
                  to: "/help",
                },
                {
                  id: "catalog",
                  label: "Catalog",
                  icon: Library,
                  to: "/catalog",
                },
                {
                  id: "archive",
                  label: "Archive (past jobs)",
                  icon: ArchiveIcon,
                  to: "/reports/archive",
                },
                user?.platform_owner && {
                  id: "prospects",
                  label: "Prospect pipeline",
                  icon: Target,
                  to: "/prospects",
                },
                (user?.is_demo_user || user?.email === "smtp-test@servicesellpro.co.uk") && {
                  id: "reset-demo",
                  label: "Reset demo workshop",
                  icon: RefreshCcw,
                  tone: "warning",
                  // Triggered via a custom DOM event — the modal lives on
                  // <DemoResetButton triggerOnly /> mounted just below.
                  onClick: () => window.dispatchEvent(new CustomEvent("ssp:open-demo-reset")),
                },
              ]}
            />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold leading-tight">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.workshop_name}</div>
            </div>
            <button onClick={() => { logout(); nav("/"); }} className="btn-outline" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
            {/* Demo-reset modal lives here, mounted invisibly — the More menu
                fires a custom event to open it. Keeps the modal lifecycle
                inside its own component without prop-drilling. */}
            {(user?.is_demo_user || user?.email === "smtp-test@servicesellpro.co.uk") && (
              <DemoResetButton triggerOnly />
            )}
          </div>
        </div>
      </header>

      <ImpersonationBanner />
      {user?.is_demo_user && <PitchDeckFab />}

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-10" data-testid="dashboard-page">
        <OnboardingBillingBanner user={user} />
        {/* First-day onboarding checklist — auto-hides once complete or dismissed */}
        <OnboardingChecklist />
        <PushOptInPrompt />

        {/* Heading + actions */}
        <div className="flex flex-wrap items-end justify-between gap-4 mt-6 first:mt-0">
          <div>
            <div className="label-overline">Dashboard</div>
            <h1
              className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2"
              data-testid="dashboard-greeting"
            >
              {greetingFor(user?.name)}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-job-btn">
              <Plus className="w-4 h-4" /> New job
            </button>
            {!user?.is_demo_user && (
              <button onClick={createDemo} disabled={demoBusy} className="btn-outline" data-testid="demo-job-btn">
                <Sparkles className="w-4 h-4" /> {demoBusy ? "Creating…" : "Try a demo"}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-border" role="tablist" aria-label="Dashboard view">
          <div className="flex gap-1 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin" data-testid="dash-tabs">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  data-testid={`dash-tab-${t.id}`}
                  className={`relative px-3 sm:px-5 py-3 inline-flex items-center gap-2 text-sm font-bold tracking-tight transition-colors whitespace-nowrap shrink-0 ${
                    active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <t.icon className={`w-4 h-4 ${active ? "text-primary" : ""}`} />
                  {t.label}
                  {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary rounded-full" />}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground py-2">
            {TABS.find((t) => t.id === tab)?.blurb}
          </div>
        </div>

        {/* Tab content */}
        <div className="mt-8" data-testid={`dash-tab-content-${tab}`}>
          {tab === "advisor" && (
            <ServiceAdvisorTab
              jobs={jobs} loading={loading} matches={matches} q={q} setQ={setQ}
              preCallPending={preCallPending}
              followUpDueCount={followUpDueCount}
              unreadMessages={unreadMessages}
              precallRepliesToday={precallRepliesToday}
              onOpenFollowUps={() => setShowFollowUps(true)}
              onOpenDiary={() => setShowDiary(true)}
              onNewJob={() => setShowNew(true)}
              onDemo={createDemo} demoBusy={demoBusy}
              nav={nav}
              onJobsChanged={refresh}
            />
          )}
          {tab === "control" && (
            <WorkshopControlTab
              jobs={jobs} loading={loading} matches={matches} q={q} setQ={setQ}
              nav={nav}
              onJobsChanged={refresh}
            />
          )}
          {tab === "technician" && (
            <TechnicianTab
              jobs={jobs} loading={loading} matches={matches} q={q} setQ={setQ}
              user={user}
              onNewJob={() => setShowNew(true)}
              nav={nav}
              onJobsChanged={refresh}
            />
          )}
          {tab === "manager" && <ManagerTab nav={nav} />}
          {tab === "ucm"     && <UsedCarManagerTab />}
        </div>
      </main>

      {showNew && <NewJobModal newJob={newJob} setNewJob={setNewJob} creating={creating} onSubmit={createJob} onClose={() => setShowNew(false)} />}
      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
      <FollowUpsModal
        open={showFollowUps}
        onClose={() => setShowFollowUps(false)}
        onOpenSettings={() => { setShowFollowUps(false); setShowSettings(true); }}
      />
      <DiaryImportWizard
        open={showDiary}
        onClose={() => setShowDiary(false)}
        onImported={refresh}
      />
    </div>
  );
}

// ------------------------- Shared bits -------------------------

function StatCard({ label, value, accent = "primary", testid, onClick }) {
  const dot = { primary: "bg-primary", success: "bg-success", warning: "bg-warning", destructive: "bg-destructive", muted: "bg-muted-foreground" }[accent];
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  const baseClass = "card-default p-5 text-left w-full block";
  const interactiveClass = interactive
    ? " transition-all hover:border-foreground/30 hover:bg-muted/30 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 cursor-pointer"
    : "";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={onClick}
      className={baseClass + interactiveClass}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 label-overline">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {label}
      </div>
      <div className="font-display text-3xl font-bold mt-2 tracking-tight">{value}</div>
    </Tag>
  );
}

function SearchBar({ q, setQ, placeholder }) {
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="input-default pl-9" data-testid="search-input" />
    </div>
  );
}

/**
 * Returns the YYYY-MM-DD bucket a job belongs to.
 * Prefers appointment_at (set by diary import / new-job form), falls back to created_at.
 */
function jobDateKey(j) {
  const raw = j.appointment_at || j.appointment_date || j.created_at || "";
  return raw.slice(0, 10);
}

/**
 * Formats the hh:mm slot off a job's appointment_at (or empty if not set).
 */
function jobTimeSlot(j) {
  const raw = j.appointment_at || "";
  if (raw.length < 16) return "";
  return raw.slice(11, 16);
}

/**
 * Formats an ISO datetime (like `latest_start_time`) as "HH:MM" in the
 * user's locale. Used by the arrivals list to surface the start-by pill.
 */
function fmtStartBy(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

/**
 * Returns a friendly label for a YYYY-MM-DD bucket relative to today.
 * - `short` (used by pills): "11/06", "12/06" — UK DD/MM. Today is highlighted via tone, not text.
 * - default (used by section headers): "Today" / "Tomorrow" / "Yesterday" / "Mon 16 Feb"
 */
function formatDayLabel(isoDate, opts = {}) {
  const { short = false } = opts;
  const target = new Date(isoDate + "T00:00:00"); target.setHours(0, 0, 0, 0);
  if (short) {
    const dd = String(target.getDate()).padStart(2, "0");
    const mm = String(target.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  const fmt = target.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return diff < 0 ? `${fmt} \u00b7 past` : fmt;
}

/**
 * Day-filter pills strip — sits above the jobs table.
 * Every distinct appointment date gets its own DD/MM pill, chronologically ordered.
 * Today is highlighted with the primary tint. Past dates use a muted tone.
 *
 * Props:
 *   rows      – full job list (un-filtered)
 *   selected  – currently selected bucket: "all" | YYYY-MM-DD
 *   onSelect  – (bucket) => void
 */
function DayFilterPills({ rows, selected, onSelect }) {
  const today = new Date().toISOString().slice(0, 10);
  // Group counts per ISO date
  const counts = {};
  for (const j of rows) {
    const k = jobDateKey(j);
    if (!k) continue;
    counts[k] = (counts[k] || 0) + 1;
  }
  const keys = Object.keys(counts).sort();
  const pills = [
    { key: "all", label: "All", count: rows.length, tone: "default" },
  ];
  for (const k of keys) {
    pills.push({
      key: k,
      label: formatDayLabel(k, { short: true }),
      count: counts[k],
      tone: k === today ? "today" : (k < today ? "muted" : "default"),
    });
  }
  // If there's only one real bucket (all jobs same day), pills add no value.
  if (pills.length <= 2) return null;
  return (
    <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 py-1 mb-3" data-testid="day-filter-pills">
      {pills.map((p) => {
        const active = p.key === selected;
        const toneClass = p.tone === "today"
          ? (active
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-primary/10 text-primary border-primary/40 hover:bg-primary/20")
          : p.tone === "muted"
          ? (active
              ? "bg-muted-foreground/30 text-foreground border-muted-foreground/40"
              : "bg-transparent text-muted-foreground border-border hover:bg-muted/40")
          : (active
              ? "bg-foreground text-background border-foreground"
              : "bg-transparent text-foreground border-border hover:bg-muted/40");
        return (
          <button
            type="button"
            key={p.key}
            onClick={() => onSelect(p.key)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors font-mono-plex ${toneClass}`}
            data-testid={`day-pill-${p.key}`}
          >
            {p.label}
            <span className="text-xs opacity-75">{p.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function JobsTable({ rows, loading, emptyTitle, emptyBody, emptyCta, nav, onDelete, groupByDate = false }) {
  // Multi-select state — keyed by job id. Lives at table-scope so it survives re-sorts.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Prune any selected ids that no longer exist (e.g. after refresh)
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set();
      const valid = new Set(rows.map((r) => r.id));
      prev.forEach((id) => { if (valid.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggleOne = (id, e) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || bulkBusy) return;
    if (!window.confirm(`Delete ${ids.length} selected job${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/jobs/bulk-delete", { confirm: "DELETE", ids });
      const n = r.data?.counts?.jobs ?? ids.length;
      toast.success(`Deleted ${n} job${n === 1 ? "" : "s"}`);
      clearSelection();
      if (onDelete) onDelete();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Bulk delete failed";
      toast.error(`Couldn't delete: ${detail}`);
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) return <div className="card-default p-10 text-center text-muted-foreground">Loading…</div>;
  if (!rows.length) return (
    <div className="card-default p-12 text-center" data-testid="empty-state">
      <Car className="w-10 h-10 mx-auto text-muted-foreground" />
      <div className="font-display font-bold text-xl mt-4">{emptyTitle}</div>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{emptyBody}</p>
      {emptyCta && <div className="mt-5 flex gap-2 justify-center">{emptyCta}</div>}
    </div>
  );
  const handleDelete = async (e, j) => {
    e.stopPropagation();
    const name = j.customer_name || j.vehicle_reg || "this job";
    if (!window.confirm(`Delete the quote for ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      toast.success(`Deleted ${name}`);
      if (onDelete) onDelete(j.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to delete";
      toast.error(detail);
    }
  };

  // Build groups (insert a date header row between groups) only if requested.
  let groups = [{ key: "_all", header: null, items: rows }];
  if (groupByDate) {
    const buckets = new Map();
    for (const j of rows) {
      const k = jobDateKey(j) || "zzzz-no-date";
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(j);
    }
    const keys = Array.from(buckets.keys()).sort();
    // Only render headers if there are 2+ distinct dates — pointless for a single bucket
    if (keys.length > 1) {
      groups = keys.map((k) => ({
        key: k,
        header: k === "zzzz-no-date"
          ? { label: "No appointment date set", subtitle: "Created jobs without a booking slot" }
          : { label: formatDayLabel(k), subtitle: new Date(k + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }), count: buckets.get(k).length },
        items: buckets.get(k),
      }));
    }
  }

  const allSelected = selectedIds.size > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className="card-default overflow-hidden" data-testid="jobs-table">
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-destructive/30 bg-destructive/[0.06]"
          data-testid="jobs-table-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-bold text-destructive">{selectedIds.size}</span>
            <span className="text-muted-foreground"> {selectedIds.size === 1 ? "job" : "jobs"} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              disabled={bulkBusy}
              className="btn-outline text-xs"
              data-testid="jobs-table-clear-selection-btn"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-xs font-bold hover:opacity-90 disabled:opacity-40 transition-opacity"
              data-testid="jobs-table-delete-selected-btn"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete selected
            </button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            {onDelete && (
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-primary cursor-pointer"
                  data-testid="jobs-table-select-all"
                  aria-label="Select all jobs"
                />
              </th>
            )}
            <th className="px-5 py-3 label-overline">Customer</th>
            <th className="px-5 py-3 label-overline hidden md:table-cell">Vehicle</th>
            <th className="px-5 py-3 label-overline">Status</th>
            <th className="px-5 py-3 label-overline text-right hidden sm:table-cell">Total</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.header && (
                <tr className="bg-gradient-to-r from-muted/40 to-transparent border-t border-border" data-testid={`date-group-header-${g.key}`}>
                  <td colSpan={onDelete ? 6 : 5} className="px-5 py-2.5">
                    <div className="flex items-baseline gap-3">
                      <span className="font-display font-bold text-sm tracking-tight">{g.header.label}</span>
                      {g.header.subtitle && <span className="text-xs text-muted-foreground">{g.header.subtitle}</span>}
                      {g.header.count != null && (
                        <span className="ml-auto text-xs label-overline">
                          {g.header.count} {g.header.count === 1 ? "booking" : "bookings"}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {g.items.map((j) => {
                const slot = jobTimeSlot(j);
                const isSelected = selectedIds.has(j.id);
                return (
                  <tr
                    key={j.id}
                    className={`border-t border-border hover:bg-muted/30 cursor-pointer transition-colors ${isSelected ? "bg-primary/5" : ""}`}
                    onClick={() => nav(`/jobs/${j.id}`)}
                    data-testid={`job-row-${j.id}`}
                  >
                    {onDelete && (
                      <td className="px-3 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleOne(j.id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-primary cursor-pointer"
                          data-testid={`job-row-select-${j.id}`}
                          aria-label={`Select job for ${j.customer_name || j.vehicle_reg || j.id}`}
                        />
                      </td>
                    )}
                    <td className="px-5 py-4">
                      <div className="font-semibold flex items-center gap-2">
                        {slot && <span className="text-xs font-mono-plex bg-primary/10 text-primary px-1.5 py-0.5 rounded shrink-0">{slot}</span>}
                        <span className="truncate">{j.customer_name || "\u2014"}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{j.customer_email || j.customer_phone || ""}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <div className="font-medium">{[j.vehicle_year, j.vehicle_make, j.vehicle_model].filter(Boolean).join(" ") || "\u2014"}</div>
                      <div className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${STATUS_STYLES[j.status] || ""}`}>
                        {j.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono-plex hidden sm:table-cell">£{(j.subtotal || 0).toFixed(2)}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      {onDelete && (
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, j)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors mr-1"
                          data-testid={`job-row-delete-btn-${j.id}`}
                          title="Delete this job"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Applies the selected day-pill filter to a rows array.
 */
function filterRowsByDayBucket(rows, bucket) {
  if (!bucket || bucket === "all") return rows;
  const today = new Date().toISOString().slice(0, 10);
  if (bucket === "past") return rows.filter((j) => {
    const k = jobDateKey(j);
    return k && k < today;
  });
  return rows.filter((j) => jobDateKey(j) === bucket);
}

// ------------------------- Service Advisor tab -------------------------

function CustomerSearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/customers/recent?limit=6").then((r) => setRecent(r.data.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const needle = q.trim();
    if (!needle) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/customers/search?q=${encodeURIComponent(needle)}&limit=8`)
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (reg) => {
    setOpen(false);
    setQ("");
    navigate(`/customers/${encodeURIComponent(reg)}`);
  };

  const showResults = open && (q.trim() ? results.length > 0 || loading : recent.length > 0);

  return (
    <div className="relative" data-testid="customer-search-bar">
      <div className="card-default p-2.5 flex items-center gap-2.5 transition-shadow focus-within:shadow-md focus-within:border-primary/40">
        <Search className="w-4 h-4 text-muted-foreground ml-2" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Find a customer by name, phone, email or reg…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="customer-search-input"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-2" />}
      </div>

      {showResults && (
        <div className="absolute left-0 right-0 top-full mt-2 z-30 card-default p-2 max-h-[420px] overflow-y-auto shadow-xl" data-testid="customer-search-results">
          {!q.trim() && (
            <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Recent customers</div>
          )}
          {(q.trim() ? results : recent).map((r) => (
            <button
              key={r.vehicle_reg + (r.latest_visit || "")}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); go(r.vehicle_reg); }}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/40 text-left transition-colors"
              data-testid={`customer-result-${r.vehicle_reg}`}
            >
              <div className="w-9 h-9 rounded-md bg-primary/10 text-primary grid place-items-center font-bold shrink-0">
                {(r.customer_name || "?").trim().charAt(0).toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.customer_name || "—"}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 truncate">
                  <span className="font-mono-plex">{r.vehicle_reg}</span>
                  <span className="truncate">· {[r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ")}</span>
                </div>
              </div>
              {q.trim() && r.lifetime_spend != null && (
                <div className="text-right text-xs shrink-0">
                  <div className="font-bold">£{Math.round(r.lifetime_spend).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground">{r.job_count} {r.job_count === 1 ? "visit" : "visits"}</div>
                </div>
              )}
            </button>
          ))}
          {q.trim() && !loading && results.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center" data-testid="customer-search-empty">No matches for "{q}"</div>
          )}
        </div>
      )}
    </div>
  );
}

function ServiceAdvisorTab({ jobs, loading, matches, q, setQ, preCallPending, followUpDueCount, unreadMessages, precallRepliesToday, onOpenFollowUps, onOpenDiary, onNewJob, onDemo, demoBusy, nav, onJobsChanged }) {
  const { user } = useAuth();
  const [dayBucket, setDayBucket] = useState("all");
  // Show jobs that need an advisor's attention: drafts to send, sent/viewed waiting on customer, partial.
  const advisorJobs = useMemo(() => {
    return jobs
      .filter((j) => ["draft", "sent", "viewed", "partial"].includes(j.status))
      .filter(matches)
      .sort((a, b) => {
        // Sort by appointment slot when both have one (chronological diary order),
        // fall back to most-recently-updated for jobs without a booking time.
        const ak = jobDateKey(a), bk = jobDateKey(b);
        if (ak && bk && ak !== bk) return ak.localeCompare(bk);
        const at = jobTimeSlot(a), bt = jobTimeSlot(b);
        if (at && bt && at !== bt) return at.localeCompare(bt);
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
  }, [jobs, matches]);
  const advisorJobsFiltered = useMemo(
    () => filterRowsByDayBucket(advisorJobs, dayBucket),
    [advisorJobs, dayBucket]
  );

  // Diagnostic jobs that haven't had the questionnaire filled yet
  const diagnosticIntake = useMemo(() => {
    return jobs.filter((j) =>
      j.job_type === "diagnostic" &&
      !["completed", "rejected"].includes(j.status) &&
      !j.questionnaire_answers
    );
  }, [jobs]);

  // Today's bookings that haven't been checked in yet — these are the "arriving customers" the advisor will tick off
  const arrivals = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const onToday = (j) => (j.appointment_date || "").slice(0, 10) === today || (j.created_at || "").slice(0, 10) === today;
    return jobs
      .filter(onToday)
      .filter((j) => !["completed", "rejected"].includes(j.status))
      .sort((a, b) => (a.appointment_time || "").localeCompare(b.appointment_time || ""));
  }, [jobs]);

  // Customers ready to leave — approved/partial/completed jobs that haven't had the thank-you fired yet
  const readyToCheckOut = useMemo(() => {
    return jobs
      .filter((j) => ["approved", "partial", "completed"].includes(j.status))
      .filter((j) => !j.checked_out_at)
      .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
  }, [jobs]);

  const stats = {
    awaiting: jobs.filter((j) => ["sent", "viewed"].includes(j.status)).length,
    won_today: jobs.filter((j) => ["approved", "partial"].includes(j.status)).length,
    revenue: jobs.reduce((s, j) => s + (j.approved_total || 0), 0),
  };

  return (
    <div className="space-y-8">
      {/* Customer brain — quick search */}
      <CustomerSearchBar />

      {/* KPI strip — each tile jumps to its destination panel */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard
          label="Pre-calls due"
          value={preCallPending}
          accent="warning"
          testid="advisor-stat-precalls"
          onClick={onOpenDiary}
        />
        <StatCard
          label="Pre-arrival replies"
          value={precallRepliesToday}
          accent={precallRepliesToday ? "primary" : "muted"}
          testid="advisor-stat-precall-replies"
          onClick={() => {
            const el = document.querySelector('[data-testid="advisor-precall-replies-inbox"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
        <StatCard
          label="Awaiting reply"
          value={stats.awaiting}
          accent="primary"
          testid="advisor-stat-awaiting"
          onClick={() => {
            const el = document.querySelector('[data-testid="advisor-quotes-section"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
        <StatCard
          label="New messages"
          value={unreadMessages}
          accent={unreadMessages ? "destructive" : "muted"}
          testid="advisor-stat-messages"
          onClick={() => {
            const el = document.querySelector('[data-testid="advisor-messages-inbox"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
        <StatCard
          label="Follow-ups due"
          value={followUpDueCount}
          accent={followUpDueCount ? "destructive" : "muted"}
          testid="advisor-stat-followups"
          onClick={onOpenFollowUps}
        />
      </div>

      {/* Tomorrow's diary + pre-calls — the morning's primary action, full-width */}
      <div className="card-default p-5 flex flex-col sm:flex-row sm:items-center gap-4 bg-gradient-to-br from-primary/5 to-transparent border-primary/30" data-testid="advisor-action-diary-card">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="grid place-items-center w-12 h-12 rounded-lg bg-primary/15 text-primary shrink-0"><Calendar className="w-6 h-6" /></span>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-primary">Morning routine</div>
            <div className="font-display font-bold text-lg mt-0.5">Upload tomorrow's diary &amp; fire pre-calls</div>
            <div className="text-xs text-muted-foreground mt-1">
              Import once, then fire pre-calls — replies appear below.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <button
            type="button"
            onClick={onOpenDiary}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-bold hover:scale-[1.02] transition-transform"
            data-testid="advisor-action-diary"
          >
            <Upload className="w-4 h-4" /> Import diary
          </button>
          <Link
            to="/pre-calls"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border hover:border-foreground/40 text-sm font-bold text-foreground transition-colors"
            data-testid="advisor-action-precalls"
          >
            <ArrowRight className="w-4 h-4" /> Review pre-calls
            {preCallPending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-warning text-warning-foreground text-[10px] font-mono-plex font-black" data-testid="advisor-precalls-pending-badge">{preCallPending}</span>
            )}
          </Link>
        </div>
      </div>

      {/* Check-in arrivals — second priority after the morning admin */}
      <CheckInPanelDashboard arrivals={arrivals} onChanged={onJobsChanged} nav={nav} />

      {/* No-shows — customers who booked in today but never turned up */}
      <NoShowAlertsPanel onChanged={onJobsChanged} nav={nav} />

      {/* Pre-arrival replies — every customer who's already sent their concierge details ahead of tomorrow */}
      <div data-testid="advisor-precall-replies-inbox" style={{ scrollMarginTop: "80px" }}>
        <PreArrivalRepliesInbox nav={nav} />
      </div>

      {/* Customer messages inbox — replies from pre-calls + "Tell my technician" notes */}
      <div data-testid="advisor-messages-inbox" style={{ scrollMarginTop: "80px" }}>
        <CustomerMessagesInbox nav={nav} />
      </div>

      {/* Customers waiting on a call-back from the advisor */}
      <CallbackQueuePanel />

      {/* Secondary action cards — follow-ups + ambient bits */}
      <div className="grid sm:grid-cols-2 gap-4">
        <button onClick={onOpenFollowUps} className="card-default p-5 hover:-translate-y-0.5 transition-all flex items-start gap-3 group text-left" data-testid="advisor-action-followups">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-destructive/15 text-destructive shrink-0 group-hover:scale-105 transition-transform"><Inbox className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-display font-bold text-base">Follow-ups inbox</div>
            <div className="text-xs text-muted-foreground mt-1">Re-engage customers who declined urgent (red) work — caring nudges, not pressure.</div>
            {followUpDueCount > 0 && (
              <div className="text-xs text-destructive font-mono-plex mt-1">{followUpDueCount} due</div>
            )}
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
        </button>

        <Link to="/analytics" className="card-default p-5 hover:-translate-y-0.5 transition-all flex items-start gap-3 group text-left" data-testid="advisor-action-analytics">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/15 text-primary shrink-0 group-hover:scale-105 transition-transform"><TrendingUp className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-display font-bold text-base">Your conversion</div>
            <div className="text-xs text-muted-foreground mt-1">How your week is shaping up — sent · viewed · authorised · revenue won.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
        </Link>
      </div>

      {/* Check-out — fire thank-you SMS+email when customer leaves */}
      <CheckOutPanelDashboard ready={readyToCheckOut} onChanged={onJobsChanged} nav={nav} />

      {/* Diagnostic intake — fill the questionnaire WITH the customer */}
      {diagnosticIntake.length > 0 && (
        <div className="card-default p-5 border-warning/40" data-testid="advisor-diagnostic-intake">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-warning" />
              <h2 className="font-display font-bold text-base">Diagnostic intake — fill with the customer</h2>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">
              {diagnosticIntake.length} awaiting
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Walk through the diagnostic questionnaire with the customer at check-in (or by phone). The completed form gets passed to the senior technician along with the job.
          </p>
          <ul className="space-y-2">
            {diagnosticIntake.slice(0, 5).map((j) => (
              <li
                key={j.id}
                onClick={() => nav(`/jobs/${j.id}`)}
                className="cursor-pointer flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-warning/30 bg-warning/5 hover:bg-warning/10 transition-colors"
                data-testid={`advisor-diagnostic-${j.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-warning/20 text-warning border border-warning/30 shrink-0">Diagnostic</span>
                  <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
                  <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
                </div>
                <button className="btn-outline text-xs">Start questionnaire <ChevronRight className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Jobs list */}
      <div data-testid="advisor-quotes-section" style={{ scrollMarginTop: "80px" }}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-display font-bold text-xl tracking-tight">Quotes the customer is on</h2>
          <div className="flex items-center gap-2">
            <SearchBar q={q} setQ={setQ} placeholder="Search customer, plate…" />
            <WipeAllJobsButton onWiped={onJobsChanged} />
          </div>
        </div>
        <DayFilterPills rows={advisorJobs} selected={dayBucket} onSelect={setDayBucket} />
        <JobsTable
          rows={advisorJobsFiltered}
          loading={loading}
          emptyTitle={dayBucket === "all" ? "Nothing waiting on a reply" : "No quotes on this day"}
          emptyBody={dayBucket === "all"
            ? "When you send a video quote, it'll show up here while the customer reviews it. Try a demo to see the flow."
            : "Try another day, or tap All to see the whole week."}
          nav={nav}
          onDelete={onJobsChanged}
          groupByDate={dayBucket === "all"}
          emptyCta={
            <>
              <button onClick={onNewJob} className="btn-primary" data-testid="empty-new-job-btn"><Plus className="w-4 h-4" /> New job</button>
              <button onClick={onDemo} disabled={demoBusy} className="btn-outline" data-testid="empty-demo-job-btn">
                <Sparkles className="w-4 h-4" /> {demoBusy ? "Creating…" : "Try a demo"}
              </button>
            </>
          }
        />
      </div>
    </div>
  );
}

// ------------------------- Check-in arrivals panel (Service Advisor) -------------------------

function CheckInPanelDashboard({ arrivals, onChanged, nav }) {
  // Local optimistic UI state for "checking in" jobs we've just hit the button on.
  const [busyId, setBusyId] = useState(null);
  const [photosJobId, setPhotosJobId] = useState(null);
  // eMaC service-plan lookup cache — key is uppercase reg, value is one of
  // { status: 'loading' | 'live' | 'none' | 'error' } so we can render the
  // right chip without re-querying eMaC on every render.
  const [planByReg, setPlanByReg] = useState({});
  const pending = arrivals.filter((j) => !j.checked_in_at);
  const checkedIn = arrivals.filter((j) => j.checked_in_at);

  // Fire an eMaC lookup for every unique reg on the arrivals list. Non-blocking,
  // silent-failure, one call per reg per session — advisor sees the chips
  // materialise within a second or two of the panel loading.
  useEffect(() => {
    const regs = [...new Set(arrivals.map((j) => (j.vehicle_reg || "").replace(/\s+/g, "").toUpperCase()).filter(Boolean))];
    const missing = regs.filter((r) => !(r in planByReg));
    if (!missing.length) return;
    setPlanByReg((prev) => {
      const next = { ...prev };
      missing.forEach((r) => { next[r] = { status: "loading" }; });
      return next;
    });
    missing.forEach(async (reg) => {
      try {
        const r = await api.get("/emac/lookup", { params: { reg } });
        const d = r?.data || {};
        const isLive = d.found && (d.is_live === true || ["Live Agreement", "Live Quote"].includes(d.status));
        setPlanByReg((prev) => ({ ...prev, [reg]: { status: isLive ? "live" : "none", data: d } }));
      } catch {
        setPlanByReg((prev) => ({ ...prev, [reg]: { status: "error" } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivals]);

  const planFor = (reg) => planByReg[(reg || "").replace(/\s+/g, "").toUpperCase()] || null;

  const handleCheckIn = async (job) => {
    setBusyId(job.id);
    try {
      const r = await api.post(`/jobs/${job.id}/check-in`, { delay_minutes: 5 });
      if (r?.data?.ok) {
        toast.success(`${firstName(job.customer_name, "Customer")} checked in — welcome will fire in 5 minutes`);
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (job) => {
    setBusyId(job.id);
    try {
      const r = await api.post(`/jobs/${job.id}/cancel-check-in`);
      if (r?.data?.ok) {
        toast.message("Check-in cancelled, welcome message stopped");
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  if (arrivals.length === 0) return null;

  return (
    <div className="card-default p-5" data-testid="advisor-checkin-panel">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <h2 className="font-display font-bold text-base">Check customers in</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">
          {pending.length} arriving · {checkedIn.length} in
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Tap "Check in" as each customer arrives. A welcome SMS &amp; email will fire automatically <span className="font-bold">5 minutes later</span> — once they've settled into the waiting area or got back to work.
      </p>
      <ul className="space-y-2">
        {pending.map((j) => (
          <li
            key={j.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/10 hover:bg-muted/20 transition-colors"
            data-testid={`advisor-arrival-${j.id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
              <span className="font-mono-plex text-xs text-muted-foreground shrink-0" title="Booked IN time">{j.appointment_time || "—"}</span>
              {j.latest_start_time && (
                <span
                  className="text-[10px] uppercase tracking-wider font-bold font-mono-plex px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30 shrink-0"
                  data-testid={`advisor-arrival-startby-${j.id}`}
                  title="Latest safe start to hit the promised OUT time"
                >
                  start by {fmtStartBy(j.latest_start_time)}
                </span>
              )}
              <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
              <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
              {j.mot_warning === "expired" && (
                <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/40 shrink-0" title={`MOT expired ${j.mot_expiry || ""}`}>
                  MOT expired
                </span>
              )}
              {j.mot_warning === "due_soon" && (
                <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/40 shrink-0" title={`MOT expires ${j.mot_expiry || ""}`}>
                  MOT in {j.mot_days_remaining}d
                </span>
              )}
              {j.job_type === "wait" && <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/30 shrink-0">Waiting</span>}
              {j.loan_car_provided && <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30 shrink-0">Loan</span>}
              {planFor(j.vehicle_reg)?.status === "live" && (
                <span
                  className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded shrink-0"
                  style={{ background: "rgba(34,211,217,0.12)", color: "#22D3D9", border: "1px solid rgba(34,211,217,0.4)" }}
                  title={`${planFor(j.vehicle_reg)?.data?.campaign_name || "Service plan"} · ${planFor(j.vehicle_reg)?.data?.status || "Live"}`}
                >
                  Plan · Live
                </span>
              )}
              {planFor(j.vehicle_reg)?.status === "none" && (
                <span
                  className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded shrink-0 bg-warning/15 text-warning border border-warning/30"
                  title="No active service plan on file — good opportunity to pitch one"
                >
                  Plan opp
                </span>
              )}
              {j.service_plan_pitch_sent_at && (
                <span
                  className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded shrink-0 bg-muted/40 text-muted-foreground border border-border"
                  title={`Plan benefits emailed ${new Date(j.service_plan_pitch_sent_at).toLocaleString("en-GB")}`}
                >
                  Pitched
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {planFor(j.vehicle_reg)?.status === "none" && !j.service_plan_pitch_sent_at && j.customer_email && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/jobs/${j.id}/pitch-service-plan`);
                      if (r?.data?.ok) {
                        toast.success(`Plan benefits emailed to ${firstName(j.customer_name, "customer")}`);
                        onChanged && onChanged();
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed to send plan pitch");
                    }
                  }}
                  className="btn-outline text-xs"
                  title="Email plan benefits + offer a quote"
                  data-testid={`pitch-plan-btn-${j.id}`}
                >
                  Pitch plan
                </button>
              )}
              <button
                type="button"
                onClick={() => setPhotosJobId(j.id)}
                className="btn-outline text-xs"
                title="Photograph vehicle condition before check-in"
                data-testid={`advisor-photos-btn-${j.id}`}
              >
                <Camera className="w-3.5 h-3.5" /> Photos
              </button>
              <button
                onClick={() => handleCheckIn(j)}
                disabled={busyId === j.id}
                className="btn-primary text-xs"
                data-testid={`advisor-checkin-btn-${j.id}`}
              >
                {busyId === j.id ? "…" : <><CheckCircle2 className="w-3.5 h-3.5" /> Check in</>}
              </button>
            </div>
          </li>
        ))}
        {checkedIn.map((j) => (
          <li
            key={j.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-success/30 bg-success/5"
            data-testid={`advisor-checkedin-${j.id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
              <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
              <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
              <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
              <ScheduledWelcomePill scheduledAt={j.welcome_scheduled_at} dispatchedAt={j.welcome_dispatched_at} />
              <SlaCountdown job={j} />
            </div>
            {!j.welcome_dispatched_at && (
              <button
                onClick={() => handleCancel(j)}
                disabled={busyId === j.id}
                className="btn-outline text-xs"
                data-testid={`advisor-cancel-checkin-btn-${j.id}`}
              >
                Cancel
              </button>
            )}
          </li>
        ))}
      </ul>
      {photosJobId && (
        <CheckInPhotosModal
          jobId={photosJobId}
          open={!!photosJobId}
          onClose={() => setPhotosJobId(null)}
        />
      )}
    </div>
  );
}

function ScheduledWelcomePill({ scheduledAt, dispatchedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (dispatchedAt) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [dispatchedAt]);
  if (dispatchedAt) {
    return (
      <span className="text-[10px] uppercase font-bold tracking-wider text-success ml-auto inline-flex items-center gap-1">
        <Send className="w-3 h-3" /> Welcome sent
      </span>
    );
  }
  if (!scheduledAt) return null;
  const ms = new Date(scheduledAt).getTime() - now;
  const mins = Math.max(0, Math.ceil(ms / 60_000));
  return (
    <span className="text-[10px] uppercase font-bold tracking-wider text-warning ml-auto inline-flex items-center gap-1" data-testid="welcome-eta">
      <Clock className="w-3 h-3" /> Welcome in {mins} min{mins === 1 ? "" : "s"}
    </span>
  );
}

// ------------------------- Check-out panel (Service Advisor) -------------------------

function CheckOutPanelDashboard({ ready, onChanged, nav }) {
  const [busyId, setBusyId] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);

  const handleCheckOut = async (job) => {
    setBusyId(job.id);
    try {
      const r = await api.post(`/jobs/${job.id}/check-out`);
      if (r?.data?.ok) {
        const log = r.data.log || [];
        const sent = log.filter((l) => l.ok).map((l) => l.channel);
        if (sent.length === 0) {
          toast.warning("Check-out recorded but no channel was configured");
        } else {
          toast.success(`Thank-you sent via ${sent.join(" + ")}`);
        }
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-out failed");
    } finally {
      setBusyId(null);
    }
  };

  if (ready.length === 0) return null;

  return (
    <div className="card-default p-5" data-testid="advisor-checkout-panel">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" />
          <h2 className="font-display font-bold text-base">Check customers out</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">
          {ready.length} ready
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Hit "Check out" as the customer is leaving. They'll get a personalised thank-you with your <span className="font-bold">exit video</span> and the <span className="font-bold">manufacturer CSI survey</span> framed around the importance of a 9 or 10. Set the URLs in <span className="font-mono-plex">Settings → Check-out</span>.
      </p>
      <ul className="space-y-2">
        {ready.slice(0, 8).map((j) => (
          <li
            key={j.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/10 hover:bg-muted/20 transition-colors"
            data-testid={`advisor-checkout-row-${j.id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
              <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
              <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
              <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded shrink-0 ${j.status === "approved" || j.status === "completed" ? "bg-success/15 text-success border border-success/30" : "bg-warning/15 text-warning border border-warning/30"}`}>
                {j.status}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setPreviewJob(j)}
                className="btn-outline text-xs"
                data-testid={`advisor-checkout-preview-btn-${j.id}`}
                title="Preview thank-you message"
              >
                Preview
              </button>
              <button
                onClick={() => handleCheckOut(j)}
                disabled={busyId === j.id}
                className="btn-primary text-xs"
                data-testid={`advisor-checkout-btn-${j.id}`}
              >
                {busyId === j.id ? "…" : <><Send className="w-3.5 h-3.5" /> Check out &amp; send</>}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {ready.length > 8 && (
        <div className="text-xs text-muted-foreground mt-2">+{ready.length - 8} more — scroll the jobs list to find them</div>
      )}

      {previewJob && (
        <CheckOutPreviewModal job={previewJob} onClose={() => setPreviewJob(null)} onSend={() => { handleCheckOut(previewJob); setPreviewJob(null); }} />
      )}
    </div>
  );
}

function CheckOutPreviewModal({ job, onClose, onSend }) {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    api.get(`/jobs/${job.id}/checkout-message`).then((r) => setMsg(r.data)).catch(() => {});
  }, [job.id]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="checkout-preview-modal">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="label-overline">Check-out message preview</div>
            <h3 className="font-display font-bold text-lg mt-1">{job.customer_name} · {job.vehicle_reg}</h3>
          </div>
          <button onClick={onClose} className="btn-outline">Close</button>
        </div>
        {!msg ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : (
          <div className="p-5 space-y-4">
            {(!msg.exit_video_url || !msg.csi_survey_url) && (
              <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
                {!msg.exit_video_url && !msg.csi_survey_url
                  ? "Heads up: exit video URL and CSI survey URL aren't set yet — message will fall back to placeholders."
                  : !msg.exit_video_url
                    ? "Heads up: exit video URL not set yet — message will fall back to a placeholder."
                    : "Heads up: CSI survey URL not set yet — message will fall back to a placeholder."}
                {' '}Set them in Settings → Check-out.
              </div>
            )}
            <div>
              <div className="label-overline mb-1">SMS</div>
              <div className="rounded-md border border-border bg-muted/20 p-3 text-sm whitespace-pre-wrap font-mono-plex" data-testid="checkout-preview-sms">{msg.sms_body}</div>
              <div className="text-[10px] mt-1 text-muted-foreground font-mono-plex">{msg.sms_body.length} chars · {msg.to_phone ? `→ ${msg.to_phone}` : "no phone on file"}</div>
            </div>
            <div>
              <div className="label-overline mb-1">Email</div>
              <div className="font-bold text-sm">{msg.email_subject}</div>
              <div className="rounded-md border border-border bg-muted/20 p-3 text-sm whitespace-pre-wrap mt-1" data-testid="checkout-preview-email">{msg.email_body}</div>
              <div className="text-[10px] mt-1 text-muted-foreground font-mono-plex">{msg.to_email ? `→ ${msg.to_email}` : "no email on file"}</div>
            </div>
          </div>
        )}
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline" data-testid="checkout-preview-cancel-btn">Cancel</button>
          <button onClick={onSend} className="btn-primary" data-testid="checkout-preview-send-btn">
            <Send className="w-4 h-4" /> Send check-out now
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Workshop Control tab -------------------------

const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };
const isActiveJob = (j) => !["completed", "rejected"].includes(j.status);
const sortQueue = (a, b) => {
  const pa = PRIORITY_RANK[a.priority || "normal"];
  const pb = PRIORITY_RANK[b.priority || "normal"];
  if (pa !== pb) return pa - pb;
  return (a.appointment_time || "").localeCompare(b.appointment_time || "");
};

function WorkshopControlTab({ jobs, loading, matches, q, setQ, nav, onJobsChanged }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const isToday = useCallback((j) => (j.created_at || "").slice(0, 10) === today || jobDateKey(j) === today, [today]);
  const [dayBucket, setDayBucket] = useState(today); // default the controller view to today's bookings
  const allDiaryJobs = useMemo(() => jobs.filter(matches).sort((a, b) => {
    const ak = jobDateKey(a), bk = jobDateKey(b);
    if (ak && bk && ak !== bk) return ak.localeCompare(bk);
    const at = jobTimeSlot(a), bt = jobTimeSlot(b);
    if (at && bt && at !== bt) return at.localeCompare(bt);
    return (a.customer_name || "").localeCompare(b.customer_name || "");
  }), [jobs, matches]);
  const todayJobs = useMemo(() => jobs.filter(isToday).filter(matches), [jobs, isToday, matches]);
  const diaryJobsFiltered = useMemo(
    () => filterRowsByDayBucket(allDiaryJobs, dayBucket),
    [allDiaryJobs, dayBucket]
  );

  // ---- Workshop loading lanes (active jobs only, grouped by job type) ----
  const lanes = useMemo(() => {
    const active = jobs.filter(isActiveJob).filter(matches);
    const wait = active.filter((j) => j.job_type === "wait").sort(sortQueue);
    const collection = active.filter((j) => j.job_type === "collection").sort(sortQueue);
    const diagnostic = active.filter((j) => j.job_type === "diagnostic").sort(sortQueue);
    const loan = active.filter((j) => j.loan_car_provided && j.job_type !== "wait").sort(sortQueue);
    const standard = active.filter((j) =>
      (!j.job_type || j.job_type === "dropoff") && !j.loan_car_provided
    ).sort(sortQueue);
    return { wait, collection, diagnostic, loan, standard };
  }, [jobs, matches]);

  // ---- Action queue: jobs that need a workshop-control decision NOW ----
  const actionQueue = useMemo(() => {
    const all = jobs.filter(matches);
    const start = all.filter((j) =>
      ["approved", "partial"].includes(j.status) &&
      j.checked_in_at && !j.ready_for_collection_at
    ).sort((a, b) => (b.approved_at || "").localeCompare(a.approved_at || ""));
    const markReady = start.filter((j) => {
      if (!j.approved_at) return false;
      const ageMin = (Date.now() - new Date(j.approved_at).getTime()) / 60000;
      return ageMin > 30;
    });
    const collect = all.filter((j) =>
      j.ready_for_collection_at && !j.completed_at && j.status !== "completed"
    ).sort((a, b) => (b.ready_for_collection_at || "").localeCompare(a.ready_for_collection_at || ""));
    return { start, markReady, collect };
  }, [jobs, matches]);

  // Funnel: total → drafts → sent/viewed → approved (won)
  const funnel = {
    drafts:   jobs.filter((j) => j.status === "draft").length,
    sent:     jobs.filter((j) => ["sent", "viewed"].includes(j.status)).length,
    won:      jobs.filter((j) => ["approved", "partial"].includes(j.status)).length,
    rejected: jobs.filter((j) => j.status === "rejected").length,
  };
  const sentBase = funnel.sent + funnel.won + funnel.rejected;
  const winRate = sentBase ? Math.round((funnel.won / sentBase) * 100) : 0;
  const revenue = jobs.reduce((s, j) => s + (j.approved_total || 0), 0);

  // Alerts: drafts older than 24h (advisor forgot to send)
  const stale = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return jobs.filter((j) => j.status === "draft" && new Date(j.updated_at || j.created_at || 0).getTime() < dayAgo);
  }, [jobs]);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Booked today" value={todayJobs.length} accent="primary" testid="control-stat-today" />
        <StatCard label="Won" value={funnel.won} accent="success" testid="control-stat-won" />
        <StatCard label="Win rate" value={`${winRate}%`} accent="primary" testid="control-stat-winrate" />
        <StatCard label="Revenue won" value={`£${revenue.toFixed(0)}`} accent="success" testid="control-stat-revenue" />
      </div>

      {/* Priority-ordered schedule — latest safe start, most urgent first */}
      <WorkshopSchedulePanel jobs={jobs} onChanged={onJobsChanged} />

      {/* Jobs awaiting workshop action — the controller's prioritised queue */}
      <ActionQueuePanel queue={actionQueue} nav={nav} onChanged={() => window.location.reload()} />

      {/* Workshop loading — lanes grouped by job type, customer-wait first */}
      <WorkshopLoadingPanel lanes={lanes} nav={nav} />

      {/* VHC processing — upload photo + generate AI quote, surfaced in Workshop Control */}
      <VhcProcessingPanel jobs={jobs} nav={nav} />

      {/* Quality Control checklist */}
      <QualityControlPanel jobs={jobs} nav={nav} />

      {/* Funnel */}
      <div className="card-default p-5" data-testid="control-funnel">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="label-overline">Quote funnel</div>
            <h2 className="font-display font-bold text-lg tracking-tight">Conversion live</h2>
          </div>
          <Link to="/analytics" className="btn-outline text-xs" data-testid="control-open-analytics">
            <TrendingUp className="w-3.5 h-3.5" /> Full analytics
          </Link>
        </div>
        <FunnelBars
          steps={[
            { label: "Drafts",   value: funnel.drafts,   accent: "muted" },
            { label: "Sent",     value: funnel.sent,     accent: "primary" },
            { label: "Won",      value: funnel.won,      accent: "success" },
            { label: "Declined", value: funnel.rejected, accent: "destructive" },
          ]}
        />
      </div>

      {/* Alerts */}
      {stale.length > 0 && (
        <div className="card-default p-5 border-warning/40" data-testid="control-alerts">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <h2 className="font-display font-bold text-base">Needs attention</h2>
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {stale.slice(0, 5).map((j) => (
              <li key={j.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-warning/5 border border-warning/20">
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="w-3.5 h-3.5 text-warning shrink-0" />
                  <span className="truncate">{j.customer_name || "—"} · {j.vehicle_reg || "—"} — draft for &gt;24h</span>
                </div>
                <button onClick={() => nav(`/jobs/${j.id}`)} className="btn-outline text-xs" data-testid={`control-alert-job-${j.id}`}>Open <ChevronRight className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Diary — pills let you flip between today, tomorrow and the rest of the week */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-display font-bold text-xl tracking-tight">Diary</h2>
          <div className="flex items-center gap-2">
            <SearchBar q={q} setQ={setQ} placeholder="Search customer, plate…" />
            <WipeAllJobsButton onWiped={onJobsChanged} />
          </div>
        </div>
        <DayFilterPills rows={allDiaryJobs} selected={dayBucket} onSelect={setDayBucket} />
        <JobsTable
          rows={diaryJobsFiltered}
          loading={loading}
          emptyTitle={dayBucket === today ? "No bookings logged for today" : "Nothing booked for this day"}
          emptyBody={dayBucket === today
            ? "Import your daily diary, or create a new job, and the workshop floor view will populate live."
            : "Try another day, or tap All to see the whole week."}
          nav={nav}
          onDelete={onJobsChanged}
          groupByDate={dayBucket === "all"}
        />
      </div>
    </div>
  );
}

const LANE_META = {
  wait:       { label: "Customer waiting",      sub: "Onsite — first priority", accent: "destructive", icon: Clock },
  collection: { label: "Collection & delivery", sub: "We collect / drop back",  accent: "primary",     icon: Car },
  diagnostic: { label: "Diagnostic",            sub: "Routes to senior tech",   accent: "warning",     icon: Wrench },
  loan:       { label: "Loan-car customers",    sub: "Customer in courtesy car", accent: "primary",    icon: Users2 },
  standard:   { label: "Standard drop-offs",    sub: "Booked in",                accent: "success",    icon: ClipboardCheck },
};

function ActionQueuePanel({ queue, nav, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const total = queue.start.length + queue.collect.length;
  if (total === 0) return null;

  const markReady = async (jobId, e) => {
    e?.stopPropagation();
    setBusyId(jobId);
    try {
      await api.post(`/jobs/${jobId}/mark-ready`);
      toast.success("Customer notified — car is ready to collect");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mark ready");
    } finally { setBusyId(null); }
  };

  const minutesSince = (iso) => {
    if (!iso) return null;
    return Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  };

  const ageChip = (min) => {
    if (min == null) return null;
    const txt = min < 60 ? `${min}m` : `${Math.floor(min/60)}h ${min%60}m`;
    const tone = min > 120 ? "destructive" : min > 60 ? "warning" : "muted";
    const cls = tone === "destructive" ? "bg-destructive/15 text-destructive border-destructive/40"
              : tone === "warning" ? "bg-warning/15 text-warning border-warning/40"
              : "bg-muted/20 text-muted-foreground border-border";
    return <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border font-mono-plex shrink-0 ${cls}`}>{txt}</span>;
  };

  const totalAuthorised = (j) => {
    const extras = (j.items || [])
      .filter((it) => it.status === "approved" || it.customer_decision === "approve")
      .reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    return Number(j.booked_service_amount || 0) + extras;
  };

  return (
    <div className="card-default p-5" data-testid="control-action-queue">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-warning" />
          <h2 className="font-display font-bold text-base">Jobs awaiting action</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">
          {queue.start.length} to start · {queue.collect.length} to collect
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Live queue of every job that needs a decision from you right now — work authorised but not yet ready, and cars ready for collection.
      </p>

      {queue.start.length > 0 && (
        <div className="space-y-2 mb-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            Customer authorised · ready to dispatch ({queue.start.length})
          </div>
          {queue.start.map((j) => {
            const m = minutesSince(j.approved_at);
            const authorised = totalAuthorised(j);
            return (
              <div key={j.id}
                   className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/10 hover:bg-muted/20 transition-colors"
                   data-testid={`action-queue-start-${j.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
                  <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
                  <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
                  {ageChip(m)}
                  <span className="ml-auto text-xs font-mono-plex text-success">£{authorised.toFixed(0)}</span>
                </div>
                <button onClick={(e) => markReady(j.id, e)} disabled={busyId === j.id}
                        className="btn-outline text-xs" data-testid={`action-queue-mark-ready-${j.id}`}>
                  {busyId === j.id ? "…" : <><Check className="w-3.5 h-3.5" /> Mark ready</>}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {queue.collect.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            Ready for collection · waiting on the customer ({queue.collect.length})
          </div>
          {queue.collect.map((j) => {
            const m = minutesSince(j.ready_for_collection_at);
            return (
              <div key={j.id}
                   className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-success/30 bg-success/5"
                   data-testid={`action-queue-collect-${j.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
                  <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                  <span className="font-semibold text-sm truncate">{j.customer_name || "—"}</span>
                  <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || ""}</span>
                  {ageChip(m)}
                </div>
                <button onClick={() => nav(`/jobs/${j.id}`)} className="btn-outline text-xs"
                        data-testid={`action-queue-open-${j.id}`}>
                  Open <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function WorkshopLoadingPanel({ lanes, nav }) {
  const order = ["wait", "collection", "diagnostic", "loan", "standard"];
  const total = order.reduce((s, k) => s + (lanes[k]?.length || 0), 0);
  // Cars physically on a ramp being worked on right this second.
  // "on_ramp" is set by the tech tapping "Start work" (or seeded for demos).
  const liveNow = order.reduce(
    (s, k) => s + (lanes[k]?.filter((j) => j.on_ramp)?.length || 0),
    0,
  );
  return (
    <div className="card-default p-5" data-testid="control-loading">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div>
          <div className="label-overline">Workshop loading</div>
          <h2 className="font-display font-bold text-lg tracking-tight">Next job in — by importance &amp; time</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {liveNow > 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-destructive/50 bg-destructive/10 pl-1.5 pr-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-destructive"
              title={`${liveNow} car${liveNow === 1 ? "" : "s"} on a ramp being worked on right now`}
              data-testid="control-live-now-pill"
            >
              <span className="relative inline-flex w-2.5 h-2.5">
                <span className="absolute inset-0 rounded-full bg-destructive/70 animate-ping" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-destructive" />
              </span>
              Live now · {liveNow}
            </span>
          )}
          <div className="text-xs text-muted-foreground font-mono-plex">{total} active</div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Customer-wait jobs go first. Within each lane, High priority then earliest booked time.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {order.map((key) => {
          const meta = LANE_META[key];
          const rows = lanes[key] || [];
          return (
            <div
              key={key}
              className={`rounded-lg border p-4 transition-colors ${
                meta.accent === "destructive" ? "border-destructive/40 bg-destructive/5" :
                meta.accent === "warning"     ? "border-warning/40 bg-warning/5" :
                meta.accent === "primary"     ? "border-primary/30 bg-primary/5" :
                                                "border-border bg-muted/20"
              }`}
              data-testid={`loading-lane-${key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <meta.icon className={`w-4 h-4 ${
                    meta.accent === "destructive" ? "text-destructive" :
                    meta.accent === "warning"     ? "text-warning" :
                    meta.accent === "primary"     ? "text-primary" : "text-success"
                  }`} />
                  <div className="font-display font-bold text-sm tracking-tight">{meta.label}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">{rows.length}</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{meta.sub}</div>

              <ul className="mt-3 space-y-2">
                {rows.slice(0, 4).map((j) => (
                  <li
                    key={j.id}
                    onClick={() => nav(`/jobs/${j.id}`)}
                    className="cursor-pointer rounded-md border border-border bg-card hover:bg-muted/40 px-3 py-2 transition-colors"
                    data-testid={`loading-job-${j.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {j.on_ramp && (
                          <span className="relative inline-flex w-2 h-2 shrink-0" title="On a ramp right now">
                            <span className="absolute inset-0 rounded-full bg-destructive/70 animate-ping" />
                            <span className="relative inline-flex w-2 h-2 rounded-full bg-destructive" />
                          </span>
                        )}
                        <div className="font-semibold text-sm truncate">{j.customer_name || "—"}</div>
                      </div>
                      {j.priority === "high" && (
                        <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/30">High</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 font-mono-plex">
                      <span>{j.vehicle_reg || "—"}</span>
                      {j.appointment_time && <span>· {j.appointment_time}</span>}
                      {j.loan_car_provided && <span className="text-primary">· loan car</span>}
                    </div>
                  </li>
                ))}
                {rows.length === 0 && (
                  <li className="text-xs text-muted-foreground italic px-1 py-1">No jobs in this lane.</li>
                )}
                {rows.length > 4 && (
                  <li className="text-[10px] text-muted-foreground px-1">+{rows.length - 4} more</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------- VHC Processing panel (Workshop Control) -------------------------

function VhcProcessingPanel({ jobs, nav }) {
  // Awaiting VHC: draft jobs without items uploaded yet
  const awaitingVhc = useMemo(() => jobs.filter((j) =>
    j.status === "draft" && (!j.items || j.items.length === 0)
  ).slice(0, 5), [jobs]);
  // Awaiting AI quote: draft jobs with items but no AI explanation yet
  const awaitingQuote = useMemo(() => jobs.filter((j) =>
    j.status === "draft" && j.items && j.items.length > 0 && !j.ai_explanation
  ).slice(0, 5), [jobs]);

  return (
    <div className="card-default p-5" data-testid="control-vhc-panel">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div>
          <div className="label-overline">VHC processing</div>
          <h2 className="font-display font-bold text-lg tracking-tight">Upload sheet photo &amp; generate quote</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">{awaitingVhc.length + awaitingQuote.length} pending</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Snap or upload the technician's VHC sheet, and let AI parse the items and write the customer explanation. Two-step workflow:
      </p>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="label-overline mb-2 inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-warning" /> Awaiting VHC photo</div>
          {awaitingVhc.length === 0 ? (
            <div className="text-xs text-muted-foreground rounded-md border border-dashed border-border p-3">All draft jobs already have items logged.</div>
          ) : (
            <ul className="space-y-2">
              {awaitingVhc.map((j) => (
                <li
                  key={j.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-warning/30 bg-warning/5 hover:bg-warning/10 cursor-pointer transition-colors"
                  onClick={() => nav(`/jobs/${j.id}?action=upload-vhc`)}
                  data-testid={`vhc-pending-${j.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{j.customer_name || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || "—"}</div>
                  </div>
                  <button className="btn-outline text-xs" data-testid={`vhc-upload-btn-${j.id}`}>
                    <Plus className="w-3 h-3" /> Upload sheet
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="label-overline mb-2 inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Awaiting AI quote</div>
          {awaitingQuote.length === 0 ? (
            <div className="text-xs text-muted-foreground rounded-md border border-dashed border-border p-3">All processed jobs have an AI explanation drafted.</div>
          ) : (
            <ul className="space-y-2">
              {awaitingQuote.map((j) => (
                <li
                  key={j.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-primary/30 bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors"
                  onClick={() => nav(`/jobs/${j.id}?action=generate-quote`)}
                  data-testid={`quote-pending-${j.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{j.customer_name || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || "—"} · {j.items?.length || 0} items</div>
                  </div>
                  <button className="btn-primary text-xs" data-testid={`quote-generate-btn-${j.id}`}>
                    <Sparkles className="w-3 h-3" /> Generate quote
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const DEFAULT_QC_STEPS = [
  { id: "final_inspection", label: "Final inspection",       d: "All work checked against the original VHC list" },
  { id: "road_test",        label: "Road test",              d: "Brake, steering and warning-light review" },
  { id: "valet",            label: "Wash, vacuum & mats",    d: "Vehicle returned cleaner than arrival" },
  { id: "documentation",    label: "Documentation sign-off", d: "Invoice, parts list, warranty terms confirmed" },
  { id: "handback",         label: "Handback to customer",   d: "Walk-through with customer before keys returned" },
];

function QualityControlPanel({ jobs, nav }) {
  // Pull jobs that are ready for QC: approved/partial/sent that haven't been completed yet.
  const queue = useMemo(() => jobs.filter((j) => ["approved", "partial"].includes(j.status)).slice(0, 5), [jobs]);

  return (
    <div className="card-default p-5" data-testid="control-qc">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div>
          <div className="label-overline">Quality control</div>
          <h2 className="font-display font-bold text-lg tracking-tight">Pre-handback checklist</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Generic — your sheet replaces this</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">5 quick checks before the keys go back. Replace with your own QC sheet when you're ready (drop the file in any time).</p>

      <div className="grid lg:grid-cols-2 gap-4">
        <ol className="space-y-2 text-sm" data-testid="qc-steps">
          {DEFAULT_QC_STEPS.map((s, i) => (
            <li key={s.id} className="flex items-start gap-3 px-3 py-2 rounded-md border border-border bg-muted/10">
              <span className="font-mono-plex text-xs font-bold text-primary mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1">
                <div className="font-semibold">{s.label}</div>
                <div className="text-xs text-muted-foreground">{s.d}</div>
              </div>
              <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
            </li>
          ))}
        </ol>

        <div>
          <div className="label-overline mb-2">Awaiting QC</div>
          {queue.length === 0 ? (
            <div className="text-sm text-muted-foreground rounded-md border border-dashed border-border p-4 text-center">
              No jobs in QC right now. Approved jobs land here for sign-off before completion.
            </div>
          ) : (
            <ul className="space-y-2">
              {queue.map((j) => (
                <li
                  key={j.id}
                  onClick={() => nav(`/jobs/${j.id}`)}
                  className="cursor-pointer rounded-md border border-border bg-card hover:bg-muted/40 px-3 py-2 transition-colors"
                  data-testid={`qc-job-${j.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{j.customer_name || "—"}</div>
                    <span className="text-[9px] uppercase font-bold tracking-wider text-success">Ready</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg || "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function FunnelBars({ steps }) {
  const max = Math.max(...steps.map((s) => s.value), 1);
  const accentBg = { muted: "bg-muted-foreground/40", primary: "bg-primary", success: "bg-success", destructive: "bg-destructive" };
  return (
    <div className="space-y-2">
      {steps.map((s) => (
        <div key={s.label} className="flex items-center gap-3" data-testid={`control-funnel-${s.label.toLowerCase()}`}>
          <div className="w-20 text-xs uppercase tracking-wider text-muted-foreground font-mono-plex">{s.label}</div>
          <div className="flex-1 h-6 rounded-md bg-muted/40 relative overflow-hidden">
            <div className={`h-full ${accentBg[s.accent]} transition-all`} style={{ width: `${(s.value / max) * 100}%` }} />
            <div className="absolute inset-0 flex items-center px-3 text-xs font-bold">{s.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------- Technician tab -------------------------

function TechnicianTab({ jobs, loading, matches, q, setQ, user, onNewJob, nav, onJobsChanged }) {
  const [dayBucket, setDayBucket] = useState("all");
  const myJobs = useMemo(() => {
    const mine = jobs.filter((j) => j.technician_id === user?.id);
    return mine.filter(matches).sort((a, b) => {
      const ak = jobDateKey(a), bk = jobDateKey(b);
      if (ak && bk && ak !== bk) return ak.localeCompare(bk);
      const at = jobTimeSlot(a), bt = jobTimeSlot(b);
      if (at && bt && at !== bt) return at.localeCompare(bt);
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });
  }, [jobs, matches, user]);
  const myJobsFiltered = useMemo(
    () => filterRowsByDayBucket(myJobs, dayBucket),
    [myJobs, dayBucket]
  );

  // VHC item urgency tally across my jobs (last 7 days)
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = useMemo(() => jobs.filter((j) => j.technician_id === user?.id && new Date(j.created_at || 0).getTime() > weekAgo), [jobs, user, weekAgo]);
  const tally = recent.reduce((acc, j) => {
    (j.items || []).forEach((it) => {
      const u = (it.urgency || "").toLowerCase();
      if (u === "green") acc.green++;
      else if (u === "amber") acc.amber++;
      else if (u === "red") acc.red++;
    });
    return acc;
  }, { green: 0, amber: 0, red: 0 });

  const active = myJobs.filter((j) => ["draft", "sent", "viewed", "partial"].includes(j.status));
  const recordable = myJobs.filter((j) => j.status === "draft" && !j.video_url);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="My active jobs" value={active.length} accent="primary" testid="tech-stat-active" />
        <StatCard label="Items graded green (7d)" value={tally.green} accent="success" testid="tech-stat-green" />
        <StatCard label="Items graded amber (7d)" value={tally.amber} accent="warning" testid="tech-stat-amber" />
        <StatCard label="Items graded red (7d)" value={tally.red} accent="destructive" testid="tech-stat-red" />
      </div>

      {/* Action cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        <button onClick={onNewJob} className="card-default p-5 hover:-translate-y-0.5 transition-all flex items-start gap-3 group text-left" data-testid="tech-action-record">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/15 text-primary shrink-0 group-hover:scale-105 transition-transform"><Video className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-display font-bold text-base">Start a walkaround</div>
            <div className="text-xs text-muted-foreground mt-1">Create a new job and jump straight into the recording flow.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
        </button>

        <div className="card-default p-5 flex items-start gap-3" data-testid="tech-action-record-pending">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-warning/15 text-warning shrink-0"><ClipboardCheck className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-display font-bold text-base">Drafts needing video</div>
            <div className="text-xs text-muted-foreground mt-1">
              {recordable.length === 0
                ? "All your drafts have a walkaround attached. Nice work."
                : `${recordable.length} draft${recordable.length === 1 ? "" : "s"} still missing a walkaround video.`}
            </div>
            {recordable.length > 0 && (
              <button onClick={() => nav(`/jobs/${recordable[0].id}`)} className="btn-outline text-xs mt-3" data-testid="tech-resume-record">
                Resume <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* My jobs */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-display font-bold text-xl tracking-tight">My jobs</h2>
          <div className="flex items-center gap-2">
            <SearchBar q={q} setQ={setQ} placeholder="Search my customers, plates…" />
            <WipeAllJobsButton onWiped={onJobsChanged} />
          </div>
        </div>
        <DayFilterPills rows={myJobs} selected={dayBucket} onSelect={setDayBucket} />
        <JobsTable
          rows={myJobsFiltered}
          loading={loading}
          emptyTitle={dayBucket === "all" ? "No jobs assigned to you yet" : "Nothing assigned to you on this day"}
          emptyBody={dayBucket === "all"
            ? "When jobs are created (or imported from the diary), they'll appear here ready to record."
            : "Try another day, or tap All to see all your work."}
          nav={nav}
          onDelete={onJobsChanged}
          groupByDate={dayBucket === "all"}
          emptyCta={
            <button onClick={onNewJob} className="btn-primary" data-testid="empty-tech-new-job-btn"><Plus className="w-4 h-4" /> New job</button>
          }
        />
      </div>
    </div>
  );
}

// ------------------------- Manager tab — analytics deep-dive -------------------------

/**
 * RRG Group site selector — cosmetic drop-down shown at the top of the
 * Manager tab for the boardroom demo tenant (Arran / Dave / Clive).
 *
 * Purpose (Friday 7 Mar 2026 pitch): demonstrate that the platform is
 * multi-site / group-capable. Real data wiring per site is a Phase 2
 * task; for the pitch we show the 11 real RRG Toyota sites so Dave
 * Owen (Group Aftersales Manager) recognises his estate immediately.
 */
const RRG_TOYOTA_SITES = [
  "Toyota Altrincham",
  "Toyota Bury",
  "Toyota Bradford",
  "Toyota Denton",
  "Toyota Rochdale",
  "Toyota Stockport",
  "Toyota Bolton",
  "Toyota Macclesfield",
  "Toyota Salford Quays",
  "Toyota Silsden",
  "Toyota Huddersfield",
];

function GroupSiteSelector() {
  const [selected, setSelected] = useState("__group__");
  const isGroup = selected === "__group__";
  const label = isGroup ? `All ${RRG_TOYOTA_SITES.length} Toyota sites` : selected;
  return (
    <div className="card-default p-5 flex items-center gap-4 flex-wrap" data-testid="group-site-selector">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-[color:var(--brand-primary,#EB0A1E)]/10 grid place-items-center shrink-0">
          <Building2 className="w-5 h-5 text-[color:var(--brand-primary,#EB0A1E)]" />
        </div>
        <div className="min-w-0">
          <div className="label-overline">RRG Group · viewing</div>
          <div className="font-display font-bold text-lg tracking-tight truncate">{label}</div>
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/40 rounded-full px-2 py-0.5">
          {RRG_TOYOTA_SITES.length} sites
        </span>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="text-sm font-medium rounded-lg border border-border bg-background px-3 py-2 pr-8 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary,#EB0A1E)]/40"
          data-testid="group-site-dropdown"
        >
          <option value="__group__">Group aggregate — all sites</option>
          <optgroup label="RRG Toyota sites">
            {RRG_TOYOTA_SITES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </optgroup>
        </select>
      </div>
    </div>
  );
}

function ManagerTab({ nav }) {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState(null);
  const [scorecard, setScorecard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/analytics?days=${days}`),
      api.get(`/upcoming-reminders`),
      api.get(`/scorecard`),
    ])
      .then(([a, b, c]) => {
        if (cancelled) return;
        setData(a.data);
        setReminders(b.data);
        setScorecard(c.data);
      })
      .catch(() => { if (!cancelled) toast.error("Failed to load analytics"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading || !data) {
    return <div className="card-default p-10 text-center text-muted-foreground">Loading analytics…</div>;
  }

  const s = data.summary || {};
  const r = data.revenue || {};
  const recon = data.reconsider || {};
  const periods = [
    { v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }, { v: 0, l: "All" },
  ];

  return (
    <div className="space-y-8" data-testid="manager-tab">
      {/* RRG Group site selector — visible for boardroom demo accounts
          (Arran, Dave, Clive). Written permission from Arran (Mar 2026)
          to use RRG name + 11 real Toyota site names. Data wiring comes
          in a later phase — for Friday's pitch this exists to show the
          app is group-capable. */}
      {user?.is_demo_user && (user?.workshop_name || "").toLowerCase().includes("rrg") && <GroupSiteSelector />}

      {/* Period switch */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Period</div>
          <h2 className="font-display font-bold text-xl tracking-tight">
            {days === 0 ? "All time" : `Last ${days} days`}
          </h2>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full bg-muted/40 p-1" role="tablist">
          {periods.map((p) => (
            <button
              key={p.v}
              onClick={() => setDays(p.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                days === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`manager-period-${p.l.toLowerCase()}`}
            >
              {p.l}
            </button>
          ))}
          <Link to="/analytics" className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="manager-open-analytics">
            <TrendingUp className="w-3 h-3" /> Full report
          </Link>
        </div>
      </div>

      {/* My Week — personal scorecard with today + 7d + personal bests */}
      <MyWeekPanel data={scorecard} />

      {/* SMS spend & volume — last 7 days */}
      <SmsCostTile />

      {/* Plan conversion — who's actually selling service plans */}
      <PlanConversionTile />

      {/* Bundle deals — customer-facing promotions Steve can edit */}
      <Link to="/settings/bundles" className="card-default p-5 flex items-start gap-3 hover:-translate-y-0.5 transition-all group" data-testid="manager-bundles-card">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/15 text-primary shrink-0 group-hover:scale-105 transition-transform">
          <Tag className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <div className="label-overline text-primary">Promotions</div>
          <div className="font-display font-bold text-base mt-0.5">Bundle deals</div>
          <p className="text-xs text-muted-foreground mt-1">
            Customer-facing promotions — "approve aircon + geo = 10% off", "4 tyres = free alignment". Fires automatically on the quote page.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
      </Link>

      {/* Data Home — enterprise data sovereignty (nightly bucket export) */}
      <Link to="/settings/data-home" className="card-default p-5 flex items-start gap-3 hover:-translate-y-0.5 transition-all group" data-testid="manager-data-home-card">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/15 text-primary shrink-0 group-hover:scale-105 transition-transform">
          <Cloud className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <div className="label-overline text-primary">Enterprise · GDPR</div>
          <div className="font-display font-bold text-base mt-0.5">Data Home</div>
          <p className="text-xs text-muted-foreground mt-1">
            Nightly mirror of every job, message, photo &amp; audit event into your own cloud bucket. Your keys, your bucket, your BI tools.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
      </Link>

      {/* Service-plan prospects — cross-reference of customers without a plan */}
      <Link to="/service-plan-prospects" className="card-default p-5 flex items-start gap-3 hover:-translate-y-0.5 transition-all group" data-testid="manager-service-plans-card">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-success/15 text-success shrink-0 group-hover:scale-105 transition-transform">
          <Sparkles className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <div className="label-overline text-success">Service-plan upsell</div>
          <div className="font-display font-bold text-base mt-0.5">Service-plan prospects</div>
          <p className="text-xs text-muted-foreground mt-1">
            Cross-references your recent customers against your service-plan database to surface anyone without an active plan — ready for a pitch.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors mt-1" />
      </Link>

      {/* Personalised dealer pitch-deck sender (platform owner / steve's outreach tool) */}
      <PitchDeckSender />

      {/* Workshop floor leaderboard — drives advisor adoption from below */}
      <LeaderboardWidget />

      {/* Speed-to-authorise — how fast customers approve */}
      <SpeedToAuthorisePanel data={data} />

      {/* Future amber reminders — pipeline of declined-but-snoozed work */}
      <UpcomingRemindersPanel data={reminders} />

      {/* Sales conversion — % of work sold by category */}
      <SalesConversionPanel data={data} />

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Quotes sent"  value={s.sent ?? 0}                                    accent="primary" testid="manager-stat-sent" />
        <StatCard label="View rate"    value={`${s.view_rate ?? 0}%`}                         accent="warning" testid="manager-stat-viewrate" />
        <StatCard label="Approval rate" value={`${s.approval_rate ?? 0}%`}                    accent="success" testid="manager-stat-approval" />
        <StatCard label="Revenue won"   value={`£${Math.round(r.approved || 0).toLocaleString()}`} accent="success" testid="manager-stat-revenue" />
      </div>

      {/* Funnel + revenue cards */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Conversion funnel */}
        <div className="card-default p-5 lg:col-span-2" data-testid="manager-funnel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-overline">Quote funnel</div>
              <h3 className="font-display font-bold text-lg tracking-tight">From sent to closed</h3>
            </div>
            <span className="text-xs text-muted-foreground font-mono-plex">{s.sent ?? 0} sent → {s.won ?? 0} won</span>
          </div>
          <FunnelBars
            steps={[
              { label: "Sent",     value: s.sent ?? 0,      accent: "primary" },
              { label: "Viewed",   value: s.viewed ?? 0,    accent: "warning" },
              { label: "Replied",  value: s.responded ?? 0, accent: "primary" },
              { label: "Won",      value: s.won ?? 0,       accent: "success" },
              { label: "Completed", value: s.completed ?? 0, accent: "success" },
            ]}
          />
        </div>

        {/* Revenue card */}
        <div className="card-default p-5" data-testid="manager-revenue">
          <div className="label-overline">Revenue</div>
          <h3 className="font-display font-bold text-lg tracking-tight mt-1">Money on the table</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <RevenueRow label="Quoted"          value={`£${Math.round(r.quoted || 0).toLocaleString()}`} accent="muted" />
            <RevenueRow label="Approved"        value={`£${Math.round(r.approved || 0).toLocaleString()}`} accent="success" />
            <RevenueRow label="Avg. quote"      value={`£${Math.round(r.average_quote || 0).toLocaleString()}`} accent="primary" />
            <RevenueRow label="Recovered (red)" value={`£${Math.round(r.recovered || 0).toLocaleString()}`} accent={r.recovered > 0 ? "success" : "muted"} />
          </dl>
        </div>
      </div>

      {/* Urgency breakdown */}
      <div className="card-default p-5" data-testid="manager-urgency">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="label-overline">Traffic-light breakdown</div>
            <h3 className="font-display font-bold text-lg tracking-tight">Approval rate by urgency</h3>
          </div>
          <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
            <RefreshCcw className="w-3 h-3" /> {recon.red_reconsidered ?? 0} red items recovered ({recon.reconsider_rate ?? 0}%)
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { id: "red",   label: "Requires attention", sub: "Urgent",   accent: "destructive", data: data.by_urgency?.red   || {} },
            { id: "amber", label: "Advisory",           sub: "Watch",    accent: "warning",     data: data.by_urgency?.amber || {} },
            { id: "green", label: "Good",               sub: "Healthy",  accent: "success",     data: data.by_urgency?.green || {} },
          ].map((row) => (
            <div key={row.id} className="rounded-md border border-border p-4" data-testid={`manager-urgency-${row.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${row.accent === "destructive" ? "bg-destructive" : row.accent === "warning" ? "bg-warning" : "bg-success"}`} />
                  <span className="font-display font-bold text-base">{row.label}</span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{row.sub}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Mini label="Total"    value={row.data.total ?? 0} />
                <Mini label="Approved" value={row.data.approved ?? 0} accent="success" />
                <Mini label="Declined" value={row.data.declined ?? 0} accent="destructive" />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Approval</span>
                <span className="font-bold font-mono-plex">{row.data.approval_rate ?? 0}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted/40 mt-1.5 overflow-hidden">
                <div
                  className={`h-full ${row.accent === "destructive" ? "bg-destructive" : row.accent === "warning" ? "bg-warning" : "bg-success"}`}
                  style={{ width: `${Math.min(100, row.data.approval_rate || 0)}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono-plex">£{Math.round(row.data.value_approved || 0).toLocaleString()} won · £{Math.round(row.data.value_quoted || 0).toLocaleString()} quoted</div>
            </div>
          ))}
        </div>
      </div>

      {/* Engagement strip */}
      <div className="grid sm:grid-cols-3 gap-3">
        <InfoCard
          icon={Eye}
          accent="warning"
          label="Customers who opened the quote"
          value={`${s.viewed ?? 0} / ${s.sent ?? 0}`}
          sub={`${s.view_rate ?? 0}% view rate`}
          testid="manager-info-views"
        />
        <InfoCard
          icon={CheckCircle2}
          accent="success"
          label="Customers who replied"
          value={`${s.responded ?? 0}`}
          sub={`${s.response_rate ?? 0}% response rate`}
          testid="manager-info-responses"
        />
        <InfoCard
          icon={Inbox}
          accent={s.follow_ups_pending ? "destructive" : "muted"}
          label="Red items awaiting follow-up"
          value={s.follow_ups_pending ?? 0}
          sub={s.follow_ups_pending ? "Action recommended" : "All caught up"}
          testid="manager-info-followups"
        />
      </div>
    </div>
  );
}

// ------------------------- My Week (Personal Scorecard) -------------------------

function MyWeekPanel({ data }) {
  if (!data) return null;
  const fmtMins = (m) => {
    if (!m || m <= 0) return "—";
    if (m < 60) return `${Math.round(m)} min`;
    if (m < 1440) return `${(m / 60).toFixed(1)} h`;
    return `${(m / 1440).toFixed(1)} d`;
  };
  const today = data.today || {};
  const week = data.week || {};
  const pb = data.personal_best || {};

  // Did today break a personal best?
  const beatSpeed = today.fastest_minutes > 0 && today.fastest_minutes <= (pb.fastest_minutes || Infinity);
  const beatValue = today.approved_value > 0 && today.approved_value >= (pb.best_day_value || 0);

  return (
    <div className="card-default p-6 relative overflow-hidden" data-testid="my-week-panel">
      <div aria-hidden className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-primary/8 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5 relative">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/15 grid place-items-center">
            <Trophy className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="label-overline">My week</div>
            <div className="font-display font-bold text-xl tracking-tight">Personal scorecard</div>
          </div>
        </div>
        <div className="text-right">
          <div className="label-overline">Personal best</div>
          <div className="font-display font-black text-lg tracking-tight">
            {fmtMins(pb.fastest_minutes)} <span className="text-muted-foreground text-xs font-medium">fastest approval</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative">
        <ScoreTile
          icon={Clock}
          accent="primary"
          label="Today · Fastest"
          value={fmtMins(today.fastest_minutes)}
          subtitle={beatSpeed ? "🔥 New personal best!" : pb.fastest_minutes ? `PB ${fmtMins(pb.fastest_minutes)}` : ""}
          highlight={beatSpeed}
          testid="myweek-today-fastest"
        />
        <ScoreTile
          icon={PoundSterling}
          accent="success"
          label="Today · Approved"
          value={`£${Math.round(today.approved_value || 0).toLocaleString()}`}
          subtitle={beatValue ? "🔥 Best day ever!" : pb.best_day_value ? `Best £${Math.round(pb.best_day_value).toLocaleString()}` : ""}
          highlight={beatValue}
          testid="myweek-today-value"
        />
        <ScoreTile
          icon={CheckCircle2}
          accent="warning"
          label="Today · Replies"
          value={today.approval_count || 0}
          subtitle={pb.best_day_jobs ? `Best ${pb.best_day_jobs} in a day` : ""}
          testid="myweek-today-replies"
        />
        <ScoreTile
          icon={Flame}
          accent="destructive"
          label="Today · Red recovered"
          value={today.red_recovered || 0}
          subtitle={pb.best_day_recovered ? `Best ${pb.best_day_recovered} in a day` : "Convince one today"}
          testid="myweek-today-red"
        />
      </div>

      {/* 7-day strip — small + complete */}
      <div className="mt-5 rounded-lg border border-border bg-muted/20 p-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm relative" data-testid="myweek-7d">
        <div className="label-overline col-span-full md:col-span-4 mb-1 flex items-center gap-2 text-primary">
          <ZapIcon className="w-3.5 h-3.5" /> Last 7 days
        </div>
        <Stat label="Approvals"      value={week.approval_count || 0} />
        <Stat label="Approved value" value={`£${Math.round(week.approved_value || 0).toLocaleString()}`} />
        <Stat label="Median speed"   value={fmtMins(week.median_minutes)} />
        <Stat label="Red recovered"  value={week.red_recovered || 0} />
      </div>
    </div>
  );
}

function ScoreTile({ icon: Icon, accent = "primary", label, value, subtitle, highlight = false, testid }) {
  const ring =
    accent === "success" ? "border-success/40" :
    accent === "warning" ? "border-warning/40" :
    accent === "destructive" ? "border-destructive/40" :
    "border-primary/40";
  const text =
    accent === "success" ? "text-success" :
    accent === "warning" ? "text-warning" :
    accent === "destructive" ? "text-destructive" :
    "text-primary";
  return (
    <div className={`rounded-lg border bg-background p-3 transition-all ${ring} ${highlight ? "ring-2 ring-primary/30 shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.45)]" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-overline">
        <Icon className={`w-3.5 h-3.5 ${text}`} />
        {label}
      </div>
      <div className={`font-display font-black text-3xl tracking-tight mt-1.5 ${text}`}>{value}</div>
      {subtitle && (
        <div className={`text-[11px] mt-1 ${highlight ? "text-primary font-semibold" : "text-muted-foreground"}`}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-base">{value}</div>
    </div>
  );
}

// ------------------------- Upcoming Amber Reminders (Manager) -------------------------

function UpcomingRemindersPanel({ data }) {
  if (!data) return null;
  if (!data.count && !data.sent && !data.opted_out) {
    return (
      <div className="card-default p-6" data-testid="reminders-empty">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-muted/40 grid place-items-center">
            <BellPlus className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <div className="label-overline">Future bookings · "Snooze, don't lose"</div>
            <div className="font-display font-bold text-lg mt-0.5">No reminders scheduled yet</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
              When a customer declines amber work, they can ask to be reminded in 3, 6 or 9 months.
              The pipeline of upcoming reminders — and the revenue you recover — will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="card-default p-6" data-testid="reminders-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/15 grid place-items-center">
            <BellPlus className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="label-overline">Future bookings</div>
            <div className="font-display font-bold text-xl tracking-tight">Snooze, don't lose</div>
          </div>
        </div>
        <div className="text-right">
          <div className="label-overline">Pipeline value</div>
          <div className="font-display font-black text-2xl tracking-tight text-primary" data-testid="reminders-pipeline-value">
            £{Math.round(data.value_pipeline || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Scheduled"  value={data.count}                                                  accent="primary" testid="reminders-kpi-count" />
        <KPI label="Sent"       value={data.sent}                                                   accent="warning" testid="reminders-kpi-sent" />
        <KPI label="Re-booked"  value={data.booked}                                                 accent="success" testid="reminders-kpi-booked" />
        <KPI label="Conversion" value={`${data.conversion_rate ?? 0}%`}                             accent="success" testid="reminders-kpi-conv" />
      </div>

      {/* Recovered revenue callout */}
      {data.value_recovered > 0 && (
        <div className="rounded-lg bg-success/10 border border-success/40 p-3 text-sm flex items-center gap-2 mb-5" data-testid="reminders-recovered">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <span>You've recovered <span className="font-bold text-success">£{Math.round(data.value_recovered).toLocaleString()}</span> of previously declined amber work via reminders.</span>
        </div>
      )}

      {/* Upcoming list */}
      <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="reminders-list">
        {data.items.slice(0, 12).map((r) => (
          <div key={`${r.job_id}-${r.item_id}`} className="rounded-lg border border-border bg-muted/20 p-3 flex items-center gap-3" data-testid={`reminder-row-${r.item_id}`}>
            <div className="w-10 h-10 rounded-md bg-background grid place-items-center shrink-0">
              <CalendarClock className={`w-4 h-4 ${r.recovered ? "text-success" : r.first_sent_at ? "text-warning" : "text-primary"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight truncate">{r.item_name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {r.customer_name || "—"} · <span className="font-mono-plex">{(r.vehicle_reg || "").toUpperCase()}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs font-semibold">{fmtDate(r.scheduled_at)}</div>
              <div className="text-[10px] text-muted-foreground">
                {r.recovered ? "✓ Re-booked" : r.first_sent_at ? "Sent · awaiting" : `In ${Math.max(0, Math.round(r.days_until))}d`}
              </div>
            </div>
            <div className="font-mono-plex font-bold text-sm shrink-0">£{Math.round(r.value)}</div>
          </div>
        ))}
        {data.count > 12 && (
          <div className="text-center text-xs text-muted-foreground py-2">+ {data.count - 12} more scheduled</div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent = "primary", testid }) {
  const ring = accent === "success" ? "border-success/40" : accent === "warning" ? "border-warning/40" : "border-primary/40";
  const text = accent === "success" ? "text-success" : accent === "warning" ? "text-warning" : "text-primary";
  return (
    <div className={`rounded-lg border ${ring} bg-background p-3`} data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-0.5 ${text}`}>{value}</div>
    </div>
  );
}

// ------------------------- Sales Conversion (Manager) -------------------------

function SalesConversionPanel({ data }) {
  const totals = data?.totals || {};
  const r = data?.revenue || {};
  const by = data?.by_urgency || {};
  const services = data?.services || [];
  const totalSoldPct = totals.value_approval_rate ?? 0;

  const urgencyRows = [
    { id: "red",   label: "Red — urgent",    accent: "destructive", v: by.red   || {} },
    { id: "amber", label: "Amber — advisory", accent: "warning",     v: by.amber || {} },
    { id: "green", label: "Green — healthy",  accent: "success",     v: by.green || {} },
  ];

  return (
    <div className="card-default p-5" data-testid="manager-sales-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="label-overline">Sales conversion</div>
          <h2 className="font-display font-bold text-lg tracking-tight">Percentage of quoted work actually sold</h2>
        </div>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <PoundSterling className="w-3 h-3" /> Value-weighted, not item-count
        </div>
      </div>

      {/* Headline number */}
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div className="rounded-lg border border-success/40 bg-success/5 p-5" data-testid="manager-sales-total">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">Total work sold</div>
          <div className="font-display font-bold text-4xl tracking-tight mt-2 text-success">{totalSoldPct}%</div>
          <div className="text-[11px] text-muted-foreground mt-1 font-mono-plex">£{Math.round(r.approved || 0).toLocaleString()} of £{Math.round(r.quoted || 0).toLocaleString()} quoted</div>
        </div>
        <div className="rounded-lg border border-border p-5" data-testid="manager-sales-amber">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">Amber sold</div>
          <div className="font-display font-bold text-4xl tracking-tight mt-2 text-warning">{by.amber?.value_approval_rate ?? 0}%</div>
          <div className="text-[11px] text-muted-foreground mt-1 font-mono-plex">£{Math.round(by.amber?.value_approved || 0).toLocaleString()} of £{Math.round(by.amber?.value_quoted || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-lg border border-border p-5" data-testid="manager-sales-red">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">Red sold</div>
          <div className="font-display font-bold text-4xl tracking-tight mt-2 text-destructive">{by.red?.value_approval_rate ?? 0}%</div>
          <div className="text-[11px] text-muted-foreground mt-1 font-mono-plex">£{Math.round(by.red?.value_approved || 0).toLocaleString()} of £{Math.round(by.red?.value_quoted || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Per-urgency progress bars */}
      <div className="mt-6">
        <div className="label-overline mb-3">By traffic-light</div>
        <div className="space-y-3" data-testid="manager-sales-urgency">
          {urgencyRows.map((row) => {
            const pct = row.v.value_approval_rate ?? 0;
            const fill = { destructive: "bg-destructive", warning: "bg-warning", success: "bg-success" }[row.accent];
            const dot  = { destructive: "bg-destructive", warning: "bg-warning", success: "bg-success" }[row.accent];
            return (
              <div key={row.id} data-testid={`manager-sales-urgency-${row.id}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="inline-flex items-center gap-2 font-semibold">
                    <span className={`w-2 h-2 rounded-full ${dot}`} />
                    {row.label}
                  </span>
                  <span className="font-mono-plex text-muted-foreground">£{Math.round(row.v.value_approved || 0).toLocaleString()} / £{Math.round(row.v.value_quoted || 0).toLocaleString()}</span>
                  <span className="font-display font-bold w-12 text-right">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                  <div className={`h-full ${fill} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Service-line breakdown */}
      <div className="mt-6 border-t border-border pt-5">
        <div className="label-overline mb-3">By service line</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="manager-sales-services">
          {services.map((svc) => {
            const pct = svc.value_approval_rate ?? 0;
            const tone = pct >= 60 ? "success" : pct >= 30 ? "warning" : "destructive";
            const fill = { destructive: "bg-destructive", warning: "bg-warning", success: "bg-success" }[tone];
            const txt  = { destructive: "text-destructive", warning: "text-warning", success: "text-success" }[tone];
            return (
              <div key={svc.id} className="rounded-lg border border-border bg-muted/10 p-4" data-testid={`manager-sales-service-${svc.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-sm">{svc.label}</div>
                  <span className={`font-display font-bold text-xl ${txt}`}>{svc.total > 0 ? `${pct}%` : "—"}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 font-mono-plex">
                  {svc.total > 0
                    ? <>£{Math.round(svc.value_approved).toLocaleString()} / £{Math.round(svc.value_quoted).toLocaleString()} · {svc.approved}/{svc.total} items</>
                    : "No items quoted in this period"}
                </div>
                <div className="h-1.5 rounded-full bg-muted/40 mt-2 overflow-hidden">
                  <div className={`h-full ${fill}`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ------------------------- Speed-to-authorise (Manager) -------------------------

const SPEED_BUCKETS = [
  { id: "lt15",  label: "< 15 min",       accent: "success" },
  { id: "lt60",  label: "< 1 hour",       accent: "success" },
  { id: "lt4h",  label: "< 4 hours",      accent: "primary" },
  { id: "lt24h", label: "Within the day", accent: "warning" },
  { id: "gt24h", label: "Next day +",     accent: "destructive" },
];

function fmtMinutes(m) {
  if (!m && m !== 0) return "—";
  if (m < 1) return "<1m";
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function SpeedToAuthorisePanel({ data }) {
  const speed = data?.speed || {};
  const today = speed.today || {};
  const period = speed.period || {};
  const dist = speed.distribution || {};
  const fastest = speed.fastest_today || [];
  const totalDist = Object.values(dist).reduce((s, n) => s + n, 0) || 1;
  const sameDay = (dist.lt15 + dist.lt60 + dist.lt4h + dist.lt24h) || 0;
  const sameDayPct = Math.round((sameDay / totalDist) * 100);

  return (
    <div className="card-default p-5" data-testid="manager-speed-panel">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div>
          <div className="label-overline">Speed to authorise</div>
          <h2 className="font-display font-bold text-lg tracking-tight">How fast customers approve</h2>
        </div>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Clock className="w-3 h-3" /> Time from “quote sent” to first customer response
        </div>
      </div>

      {/* Headline today + period numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="manager-speed-stats">
        <SpeedStat label="Median today"     value={fmtMinutes(today.median_minutes)} accent="primary"
                    sub={`${today.count || 0} approval${today.count === 1 ? "" : "s"}`}
                    testid="manager-speed-today-median" />
        <SpeedStat label="Average today"    value={fmtMinutes(today.avg_minutes)}    accent="success"
                    sub={today.fastest_minutes ? `Fastest ${fmtMinutes(today.fastest_minutes)}` : ""}
                    testid="manager-speed-today-avg" />
        <SpeedStat label="Median (period)"  value={fmtMinutes(period.median_minutes)} accent="primary"
                    sub={`${period.count || 0} total`}
                    testid="manager-speed-period-median" />
        <SpeedStat label="Same-day approval" value={`${sameDayPct}%`} accent={sameDayPct >= 70 ? "success" : "warning"}
                    sub={`${sameDay} of ${totalDist}`} testid="manager-speed-sameday" />
      </div>

      {/* Distribution bar */}
      <div className="mt-6">
        <div className="label-overline mb-2">Distribution</div>
        <div className="flex h-8 rounded-md overflow-hidden border border-border" data-testid="manager-speed-distribution">
          {SPEED_BUCKETS.map((b) => {
            const n = dist[b.id] || 0;
            const pct = (n / totalDist) * 100;
            if (pct === 0) return null;
            const bg = { success: "bg-success", primary: "bg-primary", warning: "bg-warning", destructive: "bg-destructive" }[b.accent];
            return (
              <div
                key={b.id}
                className={`${bg} flex items-center justify-center text-[10px] font-bold text-primary-foreground`}
                style={{ width: `${pct}%` }}
                title={`${b.label}: ${n} (${Math.round(pct)}%)`}
                data-testid={`manager-speed-bucket-${b.id}`}
              >
                {pct >= 8 ? `${Math.round(pct)}%` : ""}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
          {SPEED_BUCKETS.map((b) => {
            const n = dist[b.id] || 0;
            const dot = { success: "bg-success", primary: "bg-primary", warning: "bg-warning", destructive: "bg-destructive" }[b.accent];
            return (
              <div key={b.id} className="flex items-center gap-2 text-xs" data-testid={`manager-speed-legend-${b.id}`}>
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                <span className="text-muted-foreground">{b.label}</span>
                <span className="font-mono-plex font-bold ml-auto">{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fastest approvals today */}
      {fastest.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <div className="label-overline mb-2">Fastest approvals today</div>
          <ul className="space-y-2" data-testid="manager-speed-fastest">
            {fastest.map((row, i) => (
              <li key={row.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-success/30 bg-success/5">
                <span className="font-mono-plex text-xs font-bold text-success w-6">#{i + 1}</span>
                <span className="font-semibold text-sm flex-1 truncate">{row.customer_name}</span>
                <span className="text-xs text-muted-foreground font-mono-plex">{row.vehicle_reg}</span>
                <span className="font-display font-bold text-success">{fmtMinutes(row.minutes)}</span>
                <span className="text-xs text-muted-foreground font-mono-plex">£{Math.round(row.value).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {totalDist === 1 && (dist.lt15 + dist.lt60 + dist.lt4h + dist.lt24h + dist.gt24h) === 0 && (
        <div className="mt-4 text-xs text-muted-foreground italic">
          No customer approvals yet in this period — once you start sending quotes, every approval will land here with a timer.
        </div>
      )}
    </div>
  );
}

function SpeedStat({ label, value, sub, accent = "primary", testid }) {
  const text = { primary: "text-primary", success: "text-success", warning: "text-warning", destructive: "text-destructive" }[accent];
  return (
    <div className="rounded-lg border border-border p-4 bg-muted/10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex">{label}</div>
      <div className={`font-display font-bold text-2xl tracking-tight mt-1 ${text}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function RevenueRow({ label, value, accent = "primary" }) {
  const text = { primary: "text-primary", success: "text-success", warning: "text-warning", destructive: "text-destructive", muted: "text-foreground" }[accent];
  return (
    <div className="flex items-center justify-between border-t border-border pt-3 first:border-0 first:pt-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground font-mono-plex">{label}</dt>
      <dd className={`font-display font-bold text-base ${text}`}>{value}</dd>
    </div>
  );
}

function Mini({ label, value, accent }) {
  const text = { success: "text-success", destructive: "text-destructive" }[accent] || "";
  return (
    <div>
      <div className={`font-display font-bold text-lg ${text}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function InfoCard({ icon: Icon, accent, label, value, sub, testid }) {
  const bg = { primary: "bg-primary/15 text-primary border-primary/30", warning: "bg-warning/15 text-warning border-warning/30", success: "bg-success/15 text-success border-success/30", destructive: "bg-destructive/15 text-destructive border-destructive/30", muted: "bg-muted text-muted-foreground border-border" }[accent];
  return (
    <div className="card-default p-5 flex items-start gap-3" data-testid={testid}>
      <span className={`grid place-items-center w-10 h-10 rounded-lg border shrink-0 ${bg}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display font-bold text-2xl mt-1 tracking-tight">{value}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

// ------------------------- New Job modal (extracted) -------------------------

function NewJobModal({ newJob, setNewJob, creating, onSubmit, onClose }) {
  const customerNameTrimmed = (newJob.customer_name || "").trim();
  const customerNameMissing = !customerNameTrimmed;

  // Reg-lookup state \u2014 powers the "we remember this customer" auto-fill
  // so the advisor only has to type the plate on a returning car.
  const [regLookup, setRegLookup] = useState(null);
  const [regLookupBusy, setRegLookupBusy] = useState(false);
  const [appliedSnapshot, setAppliedSnapshot] = useState(null);
  // Amber "Reg lookup unavailable — enter details manually" banner. Triggered
  // when the DVSA endpoint reports `configured: false` (creds missing on this
  // environment) or the customer-lookup call throws. Silent-failure is fine
  // in preview but on production a misconfigured deploy needs a visible hint
  // so the advisor doesn't sit staring at empty fields.
  const [lookupUnavailable, setLookupUnavailable] = useState(false);

  // Concierge memory — birthday / drink / valet / GDPR / notes surfaced
  // inline the moment we recognise a returning plate. Compact by default,
  // advisor can expand to see the full profile without leaving the modal.
  const [concierge, setConcierge] = useState(null);
  const [conciergeOpen, setConciergeOpen] = useState(false);

  const handleRegLookup = async () => {
    const reg = (newJob.vehicle_reg || "").trim();
    const cleaned = reg.replace(/[^a-zA-Z0-9]/g, "");
    if (cleaned.length < 3) return;
    setRegLookupBusy(true);
    setLookupUnavailable(false);
    try {
      const r = await api.get("/customers/lookup-by-reg", { params: { reg } });
      if (r.data?.found) {
        // Snapshot the user's current field values BEFORE we touch them \u2014
        // so the Undo button can put everything back exactly as the
        // advisor had typed it.
        setAppliedSnapshot({
          customer_name: newJob.customer_name,
          customer_email: newJob.customer_email,
          customer_phone: newJob.customer_phone,
          vehicle_make: newJob.vehicle_make,
          vehicle_model: newJob.vehicle_model,
          vehicle_year: newJob.vehicle_year,
        });
        // Only pre-fill fields the advisor has left blank \u2014 NEVER overwrite
        // something they've already typed (destructive auto-fill is the
        // worst kind of UX).
        setNewJob((prev) => ({
          ...prev,
          customer_name: prev.customer_name?.trim() ? prev.customer_name : r.data.customer_name || "",
          customer_email: prev.customer_email?.trim() ? prev.customer_email : r.data.customer_email || "",
          customer_phone: prev.customer_phone?.trim() ? prev.customer_phone : r.data.customer_phone || "",
          vehicle_make: prev.vehicle_make?.trim() ? prev.vehicle_make : r.data.vehicle_make || "",
          vehicle_model: prev.vehicle_model?.trim() ? prev.vehicle_model : r.data.vehicle_model || "",
          vehicle_year: prev.vehicle_year || r.data.vehicle_year || "",
        }));
        setRegLookup(r.data);
        // Fire off the concierge profile fetch in parallel (best-effort).
        // Uses phone first, email as fallback — mirrors the backend's
        // `_resolve_profile` precedence. Silent failure keeps this a
        // "nice-to-have" that never blocks job creation.
        const cPhone = r.data.customer_phone || "";
        const cEmail = r.data.customer_email || "";
        if (cPhone || cEmail) {
          api.get("/customers/profile", { params: { phone: cPhone, email: cEmail } })
            .then((pr) => {
              const prof = pr?.data?.profile;
              if (prof) {
                setConcierge(prof);
                setConciergeOpen(false);
              }
            })
            .catch(() => { /* silent — banner just won't render */ });
        }
      } else {
        setRegLookup({ found: false });
        setConcierge(null);
      }
      // Even for a returning customer, some vehicle fields might still be
      // blank (older records missing make/model/year). ALWAYS run the DVSA
      // enrichment in the background — it's cheap, non-blocking, and only
      // fills empty fields. If nothing lands, the user is none the wiser.
      //
      // In parallel we also check eMaC for an active service plan on the
      // reg — surfaces a green "Active service plan" pill on the modal so
      // the advisor knows before the customer arrives that this is a
      // plan customer. Read-only, silent-failure.
      const [dvsaRes, emacRes] = await Promise.allSettled([
        api.get("/vehicles/dvsa-lookup", { params: { reg } }),
        api.get("/emac/lookup", { params: { reg } }),
      ]);
      if (dvsaRes.status === "fulfilled" && dvsaRes.value?.data?.found) {
        const dv = dvsaRes.value.data;
        setNewJob((prev) => ({
          ...prev,
          vehicle_make: prev.vehicle_make?.trim() ? prev.vehicle_make : (dv.vehicle_make || ""),
          vehicle_model: prev.vehicle_model?.trim() ? prev.vehicle_model : (dv.vehicle_model || ""),
          vehicle_year: prev.vehicle_year ? prev.vehicle_year : (dv.vehicle_year || ""),
        }));
        setRegLookup((current) => ({
          ...(current || {}),
          dvsa: {
            make: dv.vehicle_make,
            model: dv.vehicle_model,
            year: dv.vehicle_year,
            colour: dv.primary_colour,
            fuel: dv.fuel_type,
            mot_expiry: dv.mot_expiry_date,
          },
        }));
      } else if (dvsaRes.status === "fulfilled" && dvsaRes.value?.data?.configured === false) {
        // DVSA creds missing on this environment — surface the amber banner
        // so the advisor knows to fill make/model/year manually rather than
        // sit waiting for a lookup that will never come.
        setLookupUnavailable(true);
      }
      if (emacRes.status === "fulfilled" && emacRes.value?.data?.found) {
        setRegLookup((current) => ({
          ...(current || {}),
          emac: emacRes.value.data,
        }));
      }
    } catch {
      // Silent failure \u2014 lookup is a nice-to-have, not a blocker. Surface
      // the amber banner so the advisor knows the enrichment didn't run.
      setRegLookup(null);
      setLookupUnavailable(true);
    } finally {
      setRegLookupBusy(false);
    }
  };

  const undoRegLookup = () => {
    if (!appliedSnapshot) return;
    setNewJob((prev) => ({ ...prev, ...appliedSnapshot }));
    setAppliedSnapshot(null);
    setRegLookup(null);
    setConcierge(null);
    setConciergeOpen(false);
  };

  const lastVisitLabel = regLookup?.last_visit_at
    ? new Date(regLookup.last_visit_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  // Days-until-birthday helper — surface a 🎂 chip when it's within a month
  // so the advisor can wish the customer a happy day when they arrive.
  const bdayDaysAway = (() => {
    const iso = concierge?.birthday;
    if (!iso) return null;
    const m = iso.match(/^\d{4}-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const [, mm, dd] = m;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisYear = new Date(now.getFullYear(), Number(mm) - 1, Number(dd));
    const nextYear = new Date(now.getFullYear() + 1, Number(mm) - 1, Number(dd));
    const target = thisYear >= today ? thisYear : nextYear;
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
  })();
  const bdayChipLabel = bdayDaysAway !== null && bdayDaysAway <= 30
    ? (bdayDaysAway === 0 ? "Birthday today" : `Birthday in ${bdayDaysAway}d`)
    : null;

  const valetLabel = {
    always: "Always valet",
    never: "No valet",
    ask: "Ask about valet",
  }[concierge?.valet_preference] || null;

  // CSI (Toyota Customer Satisfaction) chip data — sourced from lookup-by-reg
  // so we never have to hit customer_profiles just for scores. Colour bands:
  //   green ≥ 9  |  amber 7-8  |  red ≤ 6
  const lastCsi = typeof regLookup?.last_csi_score === "number" ? regLookup.last_csi_score : null;
  const csiHistory = Array.isArray(regLookup?.csi_history) ? regLookup.csi_history : [];
  // Trend arrows only shown when we have 3+ scores — matches Steve's ask.
  const csiTrend = csiHistory.length >= 3
    ? [...csiHistory].reverse().map((h) => h.score)  // oldest → newest
    : null;
  const csiColour = lastCsi === null
    ? ""
    : lastCsi >= 9
      ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
      : lastCsi >= 7
        ? "bg-amber-500/10 text-amber-700 border-amber-500/30"
        : "bg-red-500/10 text-red-700 border-red-500/30";

  const conciergeHasAnything = concierge && (
    concierge.hot_drink || concierge.valet_preference || concierge.birthday
    || concierge.loan_car_notes || concierge.collection_address
    || concierge.household || concierge.pet_at_home || concierge.allergies
    || concierge.concierge_notes
  );
  // Also show the banner when we don't have concierge memory but we DO
  // have a CSI history — the whole point is to nudge the advisor to beat
  // the last score.
  const showBanner = regLookup?.found && (conciergeHasAnything || lastCsi !== null);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-lg p-6 space-y-4 animate-fade-up my-auto max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="new-job-modal">
        <div>
          <div className="label-overline">Create</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1">New job</h2>
        </div>

        {lookupUnavailable && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2.5" data-testid="reg-lookup-unavailable-banner">
            <svg className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold text-amber-700">Reg lookup unavailable</div>
              <div className="text-xs text-amber-700/80 mt-0.5">
                Couldn&rsquo;t reach the DVSA vehicle lookup service. Enter the make, model and year manually below &mdash; the job will still work perfectly.
              </div>
            </div>
          </div>
        )}

        {regLookup?.found && (
          <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 px-3 py-2.5" data-testid="reg-lookup-banner">
            <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold text-success">Returning customer &mdash; details pre-filled</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {regLookup.customer_name || "Previous record"} &middot; last visit {lastVisitLabel}
                {regLookup.match_count > 1 ? ` · ${regLookup.match_count} previous jobs` : ""}
              </div>
            </div>
            {appliedSnapshot && (
              <button type="button" onClick={undoRegLookup} className="btn-outline text-xs shrink-0" data-testid="reg-lookup-undo-btn">
                Undo
              </button>
            )}
          </div>
        )}

        {regLookup?.emac?.found && (regLookup.emac.is_live === true
            || ["Live Agreement", "Live Quote"].includes(regLookup.emac.status || "")) && (
          <div className="flex items-start gap-3 rounded-lg border border-[#22D3D9]/40 bg-[#22D3D9]/5 px-3 py-2.5" data-testid="reg-lookup-emac-banner">
            <CheckCircle2 className="w-4 h-4 text-[#22D3D9] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold text-[#22D3D9]">Active service plan on file</div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {[
                  regLookup.emac.campaign_name || regLookup.emac.plan_name || regLookup.emac.product_type || "Service Plan",
                  regLookup.emac.status,
                  regLookup.emac.customer_name,
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        )}

        {showBanner && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid="concierge-banner">
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <div className="text-sm font-semibold text-primary truncate">Customer Magic Points</div>
                    <span className="text-[10px] font-mono font-bold tracking-wider text-primary/70 shrink-0">CMPs</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConciergeOpen((v) => !v)}
                    className="text-[11px] text-primary/80 hover:text-primary flex items-center gap-1"
                    data-testid="concierge-toggle-btn"
                  >
                    {conciergeOpen ? "Hide" : "View full profile"}
                    {conciergeOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="concierge-chips">
                  {lastCsi !== null && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${csiColour}`}
                      title={`Last Toyota CSI score: ${lastCsi}/10 — let's beat it this visit`}
                      data-testid="concierge-chip-csi"
                    >
                      ⭐ Last visit: {lastCsi}/10 — beat it
                    </span>
                  )}
                  {csiTrend && (
                    <span
                      className="inline-flex items-center gap-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border px-2 py-0.5 text-[11px] font-mono"
                      title="Your CSI score history — oldest → newest"
                      data-testid="concierge-chip-csi-trend"
                    >
                      {csiTrend.map((s, i) => (
                        <span key={i} className="inline-flex items-center">
                          <span className={
                            s >= 9 ? "text-emerald-700 font-semibold" :
                            s >= 7 ? "text-amber-700 font-semibold" :
                            "text-red-700 font-semibold"
                          }>{s}</span>
                          {i < csiTrend.length - 1 && <span className="mx-0.5 text-muted-foreground/60">→</span>}
                        </span>
                      ))}
                    </span>
                  )}
                  {bdayChipLabel && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-pink-500/10 text-pink-600 border border-pink-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-birthday">
                      <Cake className="w-3 h-3" /> {bdayChipLabel}
                    </span>
                  )}
                  {concierge?.hot_drink && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-drink">
                      <Coffee className="w-3 h-3" /> {concierge.hot_drink}
                    </span>
                  )}
                  {valetLabel && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-cyan-500/10 text-cyan-700 border border-cyan-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-valet">
                      <SparklesIcon className="w-3 h-3" /> {valetLabel}
                    </span>
                  )}
                  {regLookup.gdpr_marketing_consent === true && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-gdpr-marketing-yes">
                      <ShieldCheck className="w-3 h-3" /> Marketing: yes
                    </span>
                  )}
                  {regLookup.gdpr_marketing_consent === false && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground border border-border px-2 py-0.5 text-[11px]" data-testid="concierge-chip-gdpr-marketing-no">
                      <ShieldCheck className="w-3 h-3" /> Marketing: no
                    </span>
                  )}
                  {regLookup.gdpr_share_with_oem_consent === true && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-gdpr-oem-yes">
                      <ShieldCheck className="w-3 h-3" /> OEM share: yes
                    </span>
                  )}
                  {regLookup.gdpr_share_with_oem_consent === false && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground border border-border px-2 py-0.5 text-[11px]" data-testid="concierge-chip-gdpr-oem-no">
                      <ShieldCheck className="w-3 h-3" /> OEM share: no
                    </span>
                  )}
                  {(regLookup.gdpr_marketing_consent === null || regLookup.gdpr_marketing_consent === undefined)
                    && (regLookup.gdpr_share_with_oem_consent === null || regLookup.gdpr_share_with_oem_consent === undefined) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 px-2 py-0.5 text-[11px]" data-testid="concierge-chip-gdpr-missing">
                      <AlertTriangle className="w-3 h-3" /> GDPR not captured
                    </span>
                  )}
                </div>
                {conciergeOpen && (
                  <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px]" data-testid="concierge-full">
                    {concierge?.loan_car_notes && (
                      <div><span className="text-muted-foreground">Loan car:</span> {concierge.loan_car_notes}</div>
                    )}
                    {concierge?.collection_address && (
                      <div><span className="text-muted-foreground">Collection:</span> {concierge.collection_address}</div>
                    )}
                    {concierge?.household && (
                      <div><span className="text-muted-foreground">Household:</span> {concierge.household}</div>
                    )}
                    {concierge?.pet_at_home && (
                      <div><span className="text-muted-foreground">Pets:</span> {concierge.pet_at_home}</div>
                    )}
                    {concierge?.allergies && (
                      <div><span className="text-muted-foreground">Allergies:</span> {concierge.allergies}</div>
                    )}
                    {concierge?.birthday && !bdayChipLabel && (
                      <div><span className="text-muted-foreground">Birthday:</span> {concierge.birthday}</div>
                    )}
                    {concierge?.concierge_notes && (
                      <div className="sm:col-span-2"><span className="text-muted-foreground">Notes:</span> {concierge.concierge_notes}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-medium">Customer name *</label>
            <input required value={newJob.customer_name} onChange={(e) => setNewJob({ ...newJob, customer_name: e.target.value })} className={`input-default mt-1 ${customerNameMissing ? "ring-1 ring-destructive/40 focus:ring-destructive/60" : ""}`} data-testid="new-job-customer-name" autoFocus />
            {customerNameMissing && (
              <div className="text-[11px] text-destructive mt-1" data-testid="new-job-customer-name-error">
                Customer name is required
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input type="email" value={newJob.customer_email} onChange={(e) => setNewJob({ ...newJob, customer_email: e.target.value })} className="input-default mt-1" data-testid="new-job-customer-email" />
          </div>
          <div>
            <label className="text-sm font-medium">Phone</label>
            <input value={newJob.customer_phone} onChange={(e) => setNewJob({ ...newJob, customer_phone: e.target.value })} className="input-default mt-1" data-testid="new-job-customer-phone" />
          </div>
          <div>
            <label className="text-sm font-medium">Make</label>
            <input value={newJob.vehicle_make} onChange={(e) => setNewJob({ ...newJob, vehicle_make: e.target.value })} className="input-default mt-1" data-testid="new-job-vehicle-make" />
          </div>
          <div>
            <label className="text-sm font-medium">Model</label>
            <input value={newJob.vehicle_model} onChange={(e) => setNewJob({ ...newJob, vehicle_model: e.target.value })} className="input-default mt-1" data-testid="new-job-vehicle-model" />
          </div>
          <div>
            <label className="text-sm font-medium">Year</label>
            <input type="number" value={newJob.vehicle_year} onChange={(e) => setNewJob({ ...newJob, vehicle_year: e.target.value })} className="input-default mt-1" data-testid="new-job-vehicle-year" />
          </div>
          <div>
            <label className="text-sm font-medium">Plate / Reg</label>
            <div className="relative mt-1">
              <input
                value={newJob.vehicle_reg}
                onChange={(e) => setNewJob({ ...newJob, vehicle_reg: e.target.value })}
                onBlur={handleRegLookup}
                onKeyDown={(e) => {
                  // Trigger lookup on Enter without submitting the form
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleRegLookup();
                  }
                }}
                className="input-default pr-10 uppercase"
                placeholder="e.g. MA70 SBN"
                data-testid="new-job-vehicle-reg"
              />
              <button
                type="button"
                onClick={handleRegLookup}
                disabled={regLookupBusy}
                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
                title="Look up previous customer for this registration"
                data-testid="new-job-reg-lookup-btn"
              >
                {regLookupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>
            {regLookup?.found === false && (newJob.vehicle_reg || "").trim().length >= 3 && (
              <div className="mt-1.5" data-testid="reg-lookup-no-match">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 text-blue-700 border border-blue-500/30 px-2 py-0.5 text-[11px]">
                  <UserPlus className="w-3 h-3" /> New customer
                </span>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium">Work being done <span className="text-muted-foreground font-normal text-xs">(select all that apply)</span></label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="new-job-service-categories">
              {[
                { value: "service", label: "Service" },
                { value: "mot", label: "MOT" },
                { value: "diagnostic", label: "Diagnostic work" },
                { value: "general_mechanical", label: "General mechanical" },
              ].map((opt) => {
                const selected = (newJob.service_categories || []).includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-md border transition-colors ${selected ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:bg-muted/30"}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        setNewJob((prev) => {
                          const current = prev.service_categories || [];
                          const next = selected
                            ? current.filter((v) => v !== opt.value)
                            : [...current, opt.value];
                          // Never allow zero selections — a job always has work
                          if (next.length === 0) return prev;
                          return { ...prev, service_categories: next, service_category: next[0] };
                        });
                      }}
                      className="h-4 w-4 rounded border-border"
                      data-testid={`new-job-service-category-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Priority</label>
            <select value={newJob.priority} onChange={(e) => setNewJob({ ...newJob, priority: e.target.value })} className="input-default mt-1" data-testid="new-job-priority">
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Booked time</label>
            <input value={newJob.appointment_time} onChange={(e) => setNewJob({ ...newJob, appointment_time: e.target.value })} placeholder="e.g. 09:30" className="input-default mt-1" data-testid="new-job-appointment-time" />
          </div>
          <div className="col-span-2">
            <div className="label-overline">How is the customer attending?</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/30 transition-colors">
                <input
                  type="checkbox"
                  checked={newJob.customer_waiting}
                  onChange={(e) => setNewJob({ ...newJob, customer_waiting: e.target.checked })}
                  className="h-4 w-4 rounded border-border"
                  data-testid="new-job-customer-waiting"
                />
                Waiting customer
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/30 transition-colors">
                <input
                  type="checkbox"
                  checked={newJob.loan_car_provided}
                  onChange={(e) => setNewJob({ ...newJob, loan_car_provided: e.target.checked })}
                  className="h-4 w-4 rounded border-border"
                  data-testid="new-job-loan-car"
                />
                Loan car provided
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/30 transition-colors">
                <input
                  type="checkbox"
                  checked={newJob.collection_delivery}
                  onChange={(e) => setNewJob({ ...newJob, collection_delivery: e.target.checked })}
                  className="h-4 w-4 rounded border-border"
                  data-testid="new-job-collection-delivery"
                />
                Collection &amp; delivery
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/30 transition-colors">
                <input
                  type="checkbox"
                  checked={newJob.express_service}
                  onChange={(e) => setNewJob({ ...newJob, express_service: e.target.checked })}
                  className="h-4 w-4 rounded border-border"
                  data-testid="new-job-express-service"
                />
                Express service
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-outline" data-testid="new-job-cancel-btn">Cancel</button>
          <button type="submit" disabled={creating || customerNameMissing} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="new-job-create-btn">
            {creating ? "Creating…" : "Create job"}
          </button>
        </div>
      </form>
    </div>
  );
}
